import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PiEarLight, PiHandshakeLight } from "react-icons/pi";
import { MdOutlineRoomService } from "react-icons/md";
import { IoDiamondOutline } from "react-icons/io5";
import DOMPurify from "dompurify";
import "../Dashboard/Dashboard.css";

/* -------------------------------------------------------------------------- */
/*  CONFIG                                                                    */
/* -------------------------------------------------------------------------- */

const API_BASE = "https://dash.zorbastays.com/web-backend";

// If your admin API needs a token, return the headers here.
// e.g. return { Authorization: `Bearer ${localStorage.getItem("token")}` };
const getAuthHeaders = () => ({});

/* -------------------------------------------------------------------------- */
/*  STATIC CONTENT (never changes per property)                               */
/* -------------------------------------------------------------------------- */

const STATIC = {
  logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9fc19416-6ee2-42fd-bc5a-4f0b5dc29800/public",
  amenitiesLogo: "https://dash.zorbastays.com/assets/zorba_logo.webp",
  contact: {
    website: "www.zorbastays.com",
    email: "info@zorbastays.com",
    phones: "63552-31066, 98750-28066",
  },

  curate: [
    {
      left: "27mm",
      top: "48mm",
      width: "58mm",
      mt: "3mm",
      title: ["We Choose with Care"],
      lines: [
        "Every Zorba stay is",
        "personally verified for its",
        "design, service, and sense",
        "of place.",
      ],
    },
    {
      left: "114mm",
      top: "66mm",
      width: "63mm",
      mt: "3mm",
      title: ["We Understand Your", "“Why” Before the “Where”"],
      lines: [
        "Every journey begins with",
        "intent. We craft stays that",
        "reflect your purpose, your",
        "people, and the memories",
        "you seek to create.",
      ],
    },
    {
      left: "103mm",
      top: "136mm",
      width: "63mm",
      mt: "4mm",
      title: ["We’re Your Constant", "Point of Support"],
      lines: [
        "From the first conversation",
        "to your final day, our team",
        "ensures every detail flows",
        "seamlessly.",
      ],
    },
    {
      left: "23mm",
      top: "174mm",
      width: "62mm",
      mt: "4mm",
      title: ["We Bring Access, Not", "Aggregation"],
      lines: [
        "Our collection opens doors",
        "to stays and experiences",
        "you won’t find online,",
        "offered through trust and",
        "discovery.",
      ],
    },
    {
      left: "109mm",
      top: "215mm",
      width: "65mm",
      mt: "4mm",
      title: ["Beyond Travel —", "We Stay in Touch"],
      lines: [
        "We stay connected beyond",
        "your travels - inspiring your",
        "next journey with new",
        "destinations and",
        "discoveries.",
      ],
    },
  ],

  whyTrustUs: {
    title: "Why Trust Us?",
    headingLine1: "Because your holiday deserves",
    headingLine2: "more than a Booking.",
    highlight:
      "Handpicked stays. Verified details. Trusted partners. Personal support.",
    paragraph1:
      "At Zorba Stays, we go beyond finding a place to stay. We curate villas, boutique hotels and unique stays across India, focusing on comfort, privacy and experiences that matter.",
    paragraph2Part1: "With ",
    propertiesCount: "3,100+ properties",
    paragraph2Part2:
      ", our curated collection helps you spend less time searching and more time enjoying.",
    features: [
      {
        title: "The Art of Listening",
        description:
          "At the heart of what Zorba Stays do is the art of listening, truly understanding what moves you, excites you and helps you feel at home in the world.",
        icon: PiEarLight,
      },
      {
        title: "We Serve With Intention",
        description:
          "Every detail is handled with care. Your preferences are remembered, your time is respected, your comfort is well taken care of.",
        icon: MdOutlineRoomService,
      },
      {
        title: "We Value Access Over Excess",
        description:
          "True luxury doesn't just mean extravagance. It's about access to inaccessible and extraordinary experiences that cannot be found in random bucket lists.",
        icon: PiHandshakeLight,
      },
      {
        title: "We Take Full Responsibility",
        description:
          "We commit to your experiences as if it were our own. We stay involved from your first hello to the moment you land home, to ensure your journey feels seamless, personal and deeply cared for.",
        icon: IoDiamondOutline,
      },
    ],
  },

  trustedPartners: {
    title: "Our Trusted Partners",
    items: [
      {
        name: "The Postcard Hotel",
        logo: "https://dax6u9k4peaex.cloudfront.net//assets/images/logobonw.webp",
      },
      {
        name: "stay vista",
        logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/d291d436-8c41-4a33-e263-c18f5812d500/public",
      },
      {
        name: "Tree of life",
        logo: "https://encrypted-tbn3.gstatic.com/images?q=tbn:ANd9GcQrqStB4M3KHLiyctgsqavUhVdZHhbjtRY_CK3PE4fHOU2M7RLT",
      },
      {
        name: "RAINFOREST",
        logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/5ae4f033-d4c1-454d-6c89-096f68d95600/public",
      },
      {
        name: "ITC Hotel",
        logo: "https://d2q79iu7y748jz.cloudfront.net/s/_squarelogo/256x256/44093aec8ea005db179a34c1ce232c6f",
      },
      {
        name: "Lohono stays",
        logo: "https://gos3.ibcdn.com/c1254793-5809-4f76-912f-2463357355ab.jpg",
      },
      {
        name: "hireavilla",
        logo: "https://www.hireavilla.in/images/color-logo-2.svg",
      },
      {
        name: "elivaas",
        logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/c24b0b33-3992-45fd-c13f-ed7cba096c00/public",
      },
      {
        name: "saffronstays",
        logo: "https://www.travelturtle.world/wp-content/uploads/2026/08/Horizontal_JPG_TaglineLogo-1920x873.jpeg",
      },
      {
        name: "walden",
        logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/b248ffa7-be21-4660-c744-5afbd5e10300/public",
      },
      {
        name: "Dolkhar",
        logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRC7Qy2cnCwNgnX4CoAcGhHPv1LxEieJ_g0vd3LU157tZjMufSn",
      },
      {
        name: "kavish",
        logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/357996d4-a723-46cd-f638-072cbf322f00/public",
      },
      {
        name: "Coco Shambhala",
        logo: "https://media.licdn.com/dms/image/v2/C4E0BAQFSahOYLtRjgg/company-logo_200_200/company-logo_200_200/0/1631339580135?e=2147483647&v=beta&t=wvjJY2rvMEwkXuRYl5N3-FpPcX5XG3bo1Wuke0c9Fy8",
      },
      {
        name: "Acacia villas",
        logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/e59df9f1-83da-4e41-849f-147b567c7c00/public",
      },
      {
        name: "WelcomHeritage",
        logo: "https://www.shivavilaspalacehotel.com/assets/img/right-logo.png",
      },
    ],
  },

  services: {
    bannerBg:
      "url('https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/6bbede15-8c21-42b1-d91c-cc2fde9f4a00/public')",
    link: "https://drive.google.com/file/d/1jOhgd_mzOigpKwZ9ZCWaVt_iyguQlFKa/view",
    section1: {
      first: "Feel every",
      italic: "destination",
      description:
        "Go beyond the stay and experience the destination like a local. From curated places to eat and explore to hidden gems worth discovering, our guides bring you closer to the culture, landscapes, and character of every destination.",
      bannerText: "Check Travel Guide for Your Stay",
    },
    section2: {
      first: "Concierge at",
      italic: "your service",
      description:
        "Travel, made effortless. From local experiences and private transfers to dining and special celebrations, our concierge team takes care of the details — thoughtfully, seamlessly, and with a touch of Zorba charm.",
      bannerText: "Explore Concierge Services",
    },
  },
  travelGuideLinks: {
    goa: "https://drive.google.com/file/d/1jOhgd_mzOigpKwZ9ZCWaVt_iyguQlFKa/view?usp=share_link",
    maharashtra:
      "https://drive.google.com/file/d/1aMx2ag3PxXv5crDuaZJowa00B6xXeafx/view?usp=sharing",
    rajasthan:
      "https://drive.google.com/file/d/1L40R5H7bLE6oRv77_IxEZMp5wsAQMoWB/view?usp=share_link",
  },

  conciergeLinks: {
    goa: "https://drive.google.com/file/d/1oRimikt1qiHWq2X9K2vPmqJ_YUYI3FEI/view?usp=sharing",
    maharashtra:
      "https://drive.google.com/file/d/12F7eF8qNIi0AjmYj8aK67Lbh3k0ywJZV/view?usp=sharing",
    rajasthan:
      "https://drive.google.com/file/d/1jh84Z0DRoi0Ii77fhh6vvdMJlTqwpdfu/view?usp=sharing",
  },
  aboutZorba: {
    titlePrefix: "about",
    titleSuffix: "Zorba Stays",
    headingPrefix: "Find your self",
    headingSuffix: "in nature.",
    paragraphs: [
      "Zorba was born from the slow realization that everyone we've assisted or known over the years, have all wanted very different kinds of travel experiences.",
      "But even with all the differences, what anchored them together was the singular belief in the human & nature connection, a belief that there can be very few pleasures as universal and as pure as the simple feeling of sand between your toes on a beach or the sensation of walking barefoot on fresh green grass.",
      "This has become our attempt with Zorba Stays, to assist people in having an honest beautiful experience that they've always wanted.",
      "Zorba Stays is our hope to assist people in exploring the beautiful landscapes of our country, from the warm beaches of Goa to the serene valleys of Uttarakhand and beyond. Whether someone is looking for meditation or a collective celebration- a marriage or a party, we want to invite everyone, as long as they're seeking an honest, beautiful experience. Come explore with us and realize this dream together at Zorba Stays.",
    ],
  },

  backCover: {
    logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/d4289f22-91de-411f-d677-6e92f843ea00/public",
  },
};

/* -------------------------------------------------------------------------- */
/*  IMAGE COMPRESSION                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Photo = an <img> that opts in to compression (data-compress) and remembers
 * its original URL so we can always re-compress from the source.
 * Use it for photos ONLY — never for logos / amenity icons (they need
 * transparency, which JPEG does not support).
 */
const Photo = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    data-compress
    data-original-src={src}
    className="w-full h-full object-cover"
  />
);

// Resolves { src, ok }. ok=false means the original was kept (CORS / error).
const safeCompressImage = (src, quality) =>
  new Promise((resolve) => {
    if (!src || !src.startsWith("http")) return resolve({ src, ok: false });

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        // Resolution scales with the slider: 800px at 0% -> 1800px at 100%
        const maxW = 800 + (quality / 100) * 1000;
        const scale = Math.min(1, maxW / img.naturalWidth);
        const w = Math.round(img.naturalWidth * scale);
        const h = Math.round(img.naturalHeight * scale);

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");

        // Fill with page colour so transparency never turns black
        ctx.fillStyle = "#f8f7f6";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        const q = Math.max(0.05, quality / 100);
        resolve({ src: canvas.toDataURL("image/jpeg", q), ok: true }); // throws if CORS-tainted
      } catch {
        resolve({ src, ok: false });
      }
    };

    img.onerror = () => resolve({ src, ok: false });
    img.src = src;
  });

/* -------------------------------------------------------------------------- */
/*  HELPERS                                                                   */
/* -------------------------------------------------------------------------- */

const isHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

const renderContentHtml = (raw) => {
  if (!raw) return "";
  if (!isHtml(raw)) {
    const esc = String(raw)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return `<p>${esc.replace(/\n/g, "<br>")}</p>`;
  }

  const clean = DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u"],
    ALLOWED_ATTR: [],
  });

  return (
    clean
      // non-breaking spaces -> normal spaces so lines can wrap
      .replace(/&nbsp;|\u00a0/g, " ")
      // "five-<br>bedroom" -> "five-bedroom"
      .replace(/-\s*<br\s*\/?>\s*/gi, "-")
      // a <br> in the middle of a sentence -> a space
      // (a <br> after . ! ? : ; or a quote is kept as a real line break)
      .replace(/([^.!?:;"”’>])\s*<br\s*\/?>\s*/gi, "$1 ")
  );
};

const norm = (v) => (v == null ? "" : String(v).trim().toLowerCase());
const coverTextFor = (propertytype) => {
  const cleaned = String(propertytype || "")
    .trim()
    .replace(/^\d+\s*(bhk|bedroom|bed|br)\b[\s-]*/i, "")
    .trim()
    .toLowerCase();

  return `Explore a luxury ${cleaned || "villa"}.`;
};
const destinationKey = (raw) => {
  const s = norm(raw);
  if (!s) return "";
  if (s.includes("goa")) return "goa";
  if (
    s.includes("maharashtra") ||
    s.includes("mumbai") ||
    s.includes("lonavala") ||
    s.includes("alibaug") ||
    s.includes("karjat") ||
    s.includes("panchgani") ||
    s.includes("mahabaleshwar") ||
    /\bmaha\b/.test(s)
  )
    return "maharashtra";
  if (s.includes("rajasthan") || s.includes("jaipur") || s.includes("udaipur"))
    return "rajasthan";
  return "";
};
const isGalleryCategory = (cat) => {
  const c = norm(cat);
  return (
    !c || c === "n/a" || c === "na" || c === "null" || c === "additional photos"
  );
};

const isCoverCategory = (cat) => norm(cat) === "cover photo";

const isBedBath = (cat) =>
  /^(bedroom|bathroom)\b/i.test(String(cat || "").trim());

// "Living Room" -> { first: "Living", second: "Room" }; "Pool" -> { first: "", second: "Pool" }
const splitTitle = (cat) => {
  const words = String(cat).trim().split(/\s+/);
  if (words.length === 1) return { first: "", second: words[0] };
  return {
    first: words.slice(0, -1).join(" "),
    second: words[words.length - 1],
  };
};

const titleFontSize = (text) => {
  const len = text.length;
  if (len > 24) return "8mm";
  if (len > 16) return "10.5mm";
  return "13.5mm";
};

/** Turns the raw property + amenities responses into everything the pages need. */
const buildBrochureData = (property, amenitiesList) => {
  const images = [...(property.images || [])]
    .filter((i) => i && i.imageurl)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const locationLine = [property.location, property.destination]
    .filter(Boolean)
    .join(", ");

  // Cover images
  const covers = images.filter((i) => isCoverCategory(i.category));
  const frontImage = covers[0]?.imageurl || images[0]?.imageurl || "";
  const villaImage =
    covers[1]?.imageurl ||
    images.find((i) => i.imageurl !== frontImage)?.imageurl ||
    "";

  // Amenities (keep the order of the addons string)
  const amenityMap = new Map(
    (amenitiesList || []).map((a) => [String(a.id), a]),
  );
  const amenities = String(property.addons || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((id) => amenityMap.get(id))
    .filter(Boolean)
    .map((a) => ({ name: a.name, imgSrc: a.url }));

  // Category sections + gallery
  const groups = new Map(); // key -> { label, images[] }
  const gallery = [];

  images.forEach((img) => {
    if (isCoverCategory(img.category)) return; // cover photos live on page 1 / 3
    if (isGalleryCategory(img.category)) {
      gallery.push(img.imageurl);
      return;
    }
    const label = String(img.category).trim();
    const key = norm(label);
    if (!groups.has(key)) groups.set(key, { label, images: [] });
    groups.get(key).images.push(img.imageurl);
  });

  // Page order: Pool, Living, Kitchen, Dining, (any other categories),
  // Bedroom 1..n, Bathroom 1..n, Exterior, Jacuzzi, Courtyard, Games,
  // Theatre, Gym, Garden, then the Photo Gallery (added below)
  const AFTER_BATHROOMS = [
    "exterior",
    "jacuzzi",
    "courtyard",
    "games",
    "theatre",
    "gym",
    "garden",
  ];
  const categoryRank = (label) => {
    const l = norm(label);
    if (l.startsWith("pool")) return 1;
    if (l.startsWith("living")) return 2;
    if (l.startsWith("kitchen")) return 3;
    if (l.startsWith("dining")) return 4;
    if (l.startsWith("bedroom")) return 6;
    if (l.startsWith("bathroom")) return 7;
    const idx = AFTER_BATHROOMS.findIndex((k) => l.startsWith(k));
    if (idx !== -1) return 8 + idx / 100;
    return 5; // any other category — after dining, before bedrooms
  };
  const trailingNumber = (label) => {
    const m = String(label).match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : 0;
  };

  const orderedGroups = [...groups.values()].sort((a, b) => {
    const rankDiff = categoryRank(a.label) - categoryRank(b.label);
    if (rankDiff !== 0) return rankDiff;
    // Bedroom 2 before Bedroom 10
    return trailingNumber(a.label) - trailingNumber(b.label);
  });

  const sections = [];
  orderedGroups.forEach((g, idx) => {
    if (isBedBath(g.label)) {
      sections.push({
        key: `cat-${idx}`,
        variant: "bedroom",
        title: g.label.toUpperCase(),
        text: locationLine,
        subtitle: locationLine,
        images: g.images,
      });
    } else {
      const { first, second } = splitTitle(g.label);
      sections.push({
        key: `cat-${idx}`,
        titleFirst: first,
        titleSecond: second,
        subtitle: locationLine,
        images: g.images,
      });
    }
  });

  if (gallery.length) {
    sections.push({
      key: "photo-gallery",
      variant: "photoGallery",
      isFirstPhotoGalleryGroup: true,
      subtitle: locationLine,
      images: gallery,
    });
  }

  return {
    name: property.propertyname || "",
    locationLine,
    content: property.content || "",
    propertytype: property.propertytype || "",
    conserlink: property.conserlink || "",
    destinationKey:
      destinationKey(property.destination) || destinationKey(property.location),
    frontImage,
    villaImage,
    amenities,
    sections,
  };
};

/* -------------------------------------------------------------------------- */
/*  GALLERY LAYOUT (paginates a category's images into A4 pages)              */
/* -------------------------------------------------------------------------- */

const ReusableGalleryLayout = ({ sections, continuationOnly = false }) => {
  return sections.map((section) => {
    const galleryImages = section.images || [];
    const pages = [];

    if (galleryImages.length === 0) return null;

    if (continuationOnly) {
      let index = 0;
      while (index < galleryImages.length) {
        const remaining = galleryImages.length - index;
        if (remaining >= 3) {
          pages.push({
            type: "three",
            images: galleryImages.slice(index, index + 3),
          });
          index += 3;
          if (index < galleryImages.length) {
            const afterThree = galleryImages.length - index;
            if (afterThree === 1 || afterThree === 4) {
              pages.push({
                type: "singleContinuation",
                images: galleryImages.slice(index, index + 1),
              });
              index += 1;
            }
          }
        } else if (remaining === 2) {
          pages.push({
            type: "two",
            images: galleryImages.slice(index, index + 2),
          });
          index += 2;
        } else {
          pages.push({
            type: "singleContinuation",
            images: galleryImages.slice(index, index + 1),
          });
          index += 1;
        }
      }
    } else if (galleryImages.length === 1) {
      pages.push({ type: "singleFirst", images: galleryImages.slice(0, 1) });
    } else if (galleryImages.length === 2) {
      pages.push({ type: "twoFirst", images: galleryImages.slice(0, 2) });
    } else if (galleryImages.length === 3) {
      pages.push({ type: "threeFirst", images: galleryImages.slice(0, 3) });
    } else {
      let index = 0;
      while (index < galleryImages.length) {
        const remaining = galleryImages.length - index;
        if (remaining >= 3) {
          pages.push({
            type: index === 0 ? "threeFirst" : "three",
            images: galleryImages.slice(index, index + 3),
          });
          index += 3;
          if (index < galleryImages.length) {
            const afterThree = galleryImages.length - index;
            if (afterThree === 1 || afterThree === 4) {
              pages.push({
                type: "singleContinuation",
                images: galleryImages.slice(index, index + 1),
              });
              index += 1;
            }
          }
        } else if (remaining === 2) {
          pages.push({
            type: index === 0 ? "twoFirst" : "two",
            images: galleryImages.slice(index, index + 2),
          });
          index += 2;
        } else {
          pages.push({
            type: index === 0 ? "singleFirst" : "singleContinuation",
            images: galleryImages.slice(index, index + 1),
          });
          index += 1;
        }
      }
    }

    const alt = section.titleSecond || section.title || "Gallery";

    return pages.map((page, pageIndex) => {
      const hasBedroomTitle =
        pageIndex === 0 && !continuationOnly && section.variant === "bedroom";

      const hasStandardTitle =
        pageIndex === 0 &&
        !continuationOnly &&
        section.variant !== "bedroom" &&
        section.variant !== "photoGallery";

      const isPhotoGalleryFirstPage =
        section.variant === "photoGallery" &&
        section.isFirstPhotoGalleryGroup &&
        pageIndex === 0;

      const isWithMainTitle =
        hasBedroomTitle || hasStandardTitle || isPhotoGalleryFirstPage;

      const standardFull =
        `${section.titleFirst || ""} ${section.titleSecond || ""}`.trim();

      return (
        <div
          key={`${section.key}-${pageIndex}`}
          className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden"
        >
          {/* BEDROOM / BATHROOM TITLE */}
          {hasBedroomTitle && (
            <div className="absolute left-[20mm] top-[26mm] z-20">
              <h2
                className="PlayfairDisplay text-gray-800 leading-none"
                style={{
                  fontSize: "6.5mm",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                }}
              >
                {section.title}
              </h2>
              <p
                className="montserrat text-gray-700 uppercase"
                style={{
                  marginTop: "3mm",
                  fontSize: "2.4mm",
                  letterSpacing: "0.18em",
                  fontWeight: 500,
                }}
              >
                {section.text}
              </p>
            </div>
          )}

          {/* STANDARD LEFT TITLE */}
          {hasStandardTitle && (
            <div className="absolute left-[20mm] top-[22mm] z-20">
              <div className="flex items-baseline leading-none">
                {section.titleFirst && (
                  <span
                    className="CormorantGaramond"
                    style={{
                      fontSize: titleFontSize(standardFull),
                      fontWeight: 600,
                    }}
                  >
                    {section.titleFirst}
                  </span>
                )}
                <span
                  className="CormorantItalic"
                  style={{
                    fontSize: titleFontSize(standardFull),
                    fontWeight: 600,
                    marginLeft: section.titleFirst ? "2mm" : 0,
                  }}
                >
                  {section.titleSecond}
                </span>
              </div>
              <p
                className="montserrat text-gray-700 uppercase"
                style={{
                  marginTop: "3.5mm",
                  fontSize: "2.8mm",
                  letterSpacing: "0.18em",
                  fontWeight: 500,
                }}
              >
                {section.subtitle}
              </p>
            </div>
          )}

          {/* PHOTO GALLERY (first page) */}
          {isPhotoGalleryFirstPage && (
            <div className="absolute left-[20mm] top-[22mm] z-20">
              <div className="flex items-baseline leading-none">
                <span
                  className="CormorantGaramond"
                  style={{ fontSize: "13.5mm", fontWeight: 600 }}
                >
                  Photo
                </span>
                <span
                  className="CormorantItalic ml-[2mm]"
                  style={{ fontSize: "13.5mm", fontWeight: 600 }}
                >
                  gallery
                </span>
              </div>
              <p
                className="montserrat text-gray-700 uppercase"
                style={{
                  marginTop: "3.5mm",
                  fontSize: "2.8mm",
                  letterSpacing: "0.18em",
                  fontWeight: 500,
                }}
              >
                {section.subtitle}
              </p>
            </div>
          )}

          {/* SINGLE IMAGE (FIRST PAGE) */}
          {page.type === "singleFirst" && isWithMainTitle && (
            <div
              className="absolute overflow-hidden"
              style={{
                left: "20mm",
                top: "54mm",
                width: "170mm",
                height: "221mm",
              }}
            >
              <Photo src={page.images[0]} alt={alt} />
            </div>
          )}

          {/* TWO IMAGES (WITH MAIN HEADING) */}
          {page.type === "twoFirst" && isWithMainTitle && (
            <div
              className="absolute"
              style={{
                left: "20mm",
                top: "54mm",
                width: "170mm",
                height: "221mm",
              }}
            >
              <div
                className="absolute left-0 top-0 w-full overflow-hidden"
                style={{ height: "108.5mm" }}
              >
                <Photo src={page.images[0]} alt={alt} />
              </div>
              <div
                className="absolute left-0 w-full overflow-hidden"
                style={{ top: "112.5mm", height: "108.5mm" }}
              >
                <Photo src={page.images[1]} alt={alt} />
              </div>
            </div>
          )}

          {/* THREE IMAGES (WITH MAIN HEADING) */}
          {page.type === "threeFirst" && isWithMainTitle && (
            <div
              className="absolute"
              style={{
                left: "20mm",
                top: "54mm",
                width: "170mm",
                height: "221mm",
              }}
            >
              <div
                className="absolute left-0 top-0 w-full overflow-hidden"
                style={{ height: "120mm" }}
              >
                <Photo src={page.images[0]} alt={alt} />
              </div>
              <div
                className="absolute left-0 overflow-hidden"
                style={{ top: "124mm", width: "83mm", height: "97mm" }}
              >
                {page.images[1] && <Photo src={page.images[1]} alt={alt} />}
              </div>
              <div
                className="absolute right-0 overflow-hidden"
                style={{ top: "124mm", width: "83mm", height: "97mm" }}
              >
                {page.images[2] && <Photo src={page.images[2]} alt={alt} />}
              </div>
            </div>
          )}

          {/* THREE IMAGES */}
          {(page.type === "three" ||
            (page.type === "threeFirst" && !isWithMainTitle)) && (
            <div
              className="absolute"
              style={{
                left: "20mm",
                top: "20mm",
                width: "170mm",
                height: "257mm",
              }}
            >
              <div
                className="absolute left-0 top-0 w-full overflow-hidden"
                style={{ height: "140mm" }}
              >
                <Photo src={page.images[0]} alt={alt} />
              </div>
              <div
                className="absolute left-0 overflow-hidden"
                style={{ top: "144mm", width: "83mm", height: "113mm" }}
              >
                <Photo src={page.images[1]} alt={alt} />
              </div>
              <div
                className="absolute right-0 overflow-hidden"
                style={{ top: "144mm", width: "83mm", height: "113mm" }}
              >
                <Photo src={page.images[2]} alt={alt} />
              </div>
            </div>
          )}

          {/* TWO IMAGES */}
          {(page.type === "two" ||
            (page.type === "twoFirst" && !isWithMainTitle)) && (
            <div
              className="absolute"
              style={{
                left: "20mm",
                top: "20mm",
                width: "170mm",
                height: "257mm",
              }}
            >
              <div
                className="absolute left-0 top-0 w-full overflow-hidden"
                style={{ height: "126.5mm" }}
              >
                <Photo src={page.images[0]} alt={alt} />
              </div>
              <div
                className="absolute left-0 w-full overflow-hidden"
                style={{ top: "130.5mm", height: "126.5mm" }}
              >
                <Photo src={page.images[1]} alt={alt} />
              </div>
            </div>
          )}

          {/* SINGLE IMAGE */}
          {page.type === "singleFirst" && !isWithMainTitle && (
            <div
              className="absolute overflow-hidden"
              style={{
                left: "20mm",
                top: "20mm",
                width: "170mm",
                height: "257mm",
              }}
            >
              <Photo src={page.images[0]} alt={alt} />
            </div>
          )}

          {/* FULL BLEED CONTINUATION */}
          {page.type === "singleContinuation" && (
            <div
              className="absolute overflow-hidden"
              style={{
                left: "1mm",
                top: "1mm",
                width: "208mm",
                height: "295mm",
              }}
            >
              <Photo src={page.images[0]} alt={alt} />
            </div>
          )}
        </div>
      );
    });
  });
};

/* -------------------------------------------------------------------------- */
/*  MAIN COMPONENT                                                            */
/* -------------------------------------------------------------------------- */

const NewQuickBrochure = () => {
  const { propertyid: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const propertyid = routeId || searchParams.get("propertyid");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!propertyid) {
      setError("No property id provided.");
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const opts = { signal: controller.signal, headers: getAuthHeaders() };
        const [propRes, amenRes] = await Promise.all([
          fetch(`${API_BASE}/admin/properties/${propertyid}`, opts),
          fetch(`${API_BASE}/getAmenities`, opts),
        ]);

        if (!propRes.ok)
          throw new Error(`Property request failed (${propRes.status})`);
        if (!amenRes.ok)
          throw new Error(`Amenities request failed (${amenRes.status})`);

        const propJson = await propRes.json();
        const amenJson = await amenRes.json();

        // Support both a bare object and a { success, data } wrapper
        const property =
          propJson?.data && !propJson.propertyname ? propJson.data : propJson;
        const amenities = Array.isArray(amenJson)
          ? amenJson
          : amenJson?.data || [];

        setData(buildBrochureData(property, amenities));
      } catch (err) {
        if (err.name !== "AbortError")
          setError(err.message || "Something went wrong.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [propertyid]);

  const amenityPages = useMemo(() => {
    if (!data) return [];
    const items = data.amenities;
    return Array.from({ length: Math.ceil(items.length / 16) }, (_, i) =>
      items.slice(i * 16, i * 16 + 16),
    );
  }, [data]);

  const [quality, setQuality] = useState(75);
  const [preparing, setPreparing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleDownload = async () => {
    if (preparing) return;
    setPreparing(true);

    try {
      // Only photos (opted in via <Photo />) are compressed.
      // Always compress from the ORIGINAL url so changing the slider and
      // downloading again works in the same session.
      const imgs = Array.from(
        document.querySelectorAll(".print-page img[data-compress]"),
      );

      setProgress({ current: 0, total: imgs.length });

      let count = 0;
      let failed = 0;
      for (const img of imgs) {
        const original = img.dataset.originalSrc || img.getAttribute("src");
        const result = await safeCompressImage(original, quality);

        img.setAttribute("src", result.src);
        if (!result.ok) {
          failed++;
          console.warn("Not compressed (CORS?):", original);
        }

        count++;
        setProgress({ current: count, total: imgs.length });
      }
      if (failed) console.warn(`${failed} image(s) could not be compressed`);

      // Wait for all images and fonts to load
      await Promise.race([
        Promise.all([
          ...Array.from(document.querySelectorAll(".print-page img")).map(
            (img) =>
              img.complete
                ? Promise.resolve()
                : new Promise((r) => {
                    img.addEventListener("load", r, { once: true });
                    img.addEventListener("error", r, { once: true });
                  }),
          ),
          document.fonts?.ready ?? Promise.resolve(),
        ]),
        new Promise((r) => setTimeout(r, 30000)), // 30s timeout
      ]);

      const safeName = String(data?.name || "Property")
        .replace(/[\\/:*?"<>|]/g, "")
        .trim();
      document.title = `${safeName}-QuickBrochure`;

      window.print();
    } finally {
      setPreparing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-gray-600">
        Loading brochure…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-red-600">
        {error || "Unable to load brochure."}
      </div>
    );
  }

  const {
    name,
    locationLine,
    content,
    frontImage,
    villaImage,
    propertytype,
    conserlink,
    destinationKey: propDestinationKey,
    sections,
  } = data;
  const { contact } = STATIC;
  const coverText = coverTextFor(propertytype);

  return (
    <div className="bg-white min-h-screen flex flex-col items-center py-10 gap-10 print:gap-0 print:py-0 print:p-0 print:bg-[#f8f7f6]">
      {/* TOOLBAR — hidden when printing */}
      <div className="print:hidden w-[210mm] flex flex-col gap-4 mb-4 px-2">
        <div className="flex items-center justify-between gap-8">
          {/* Quality Slider (0-100) */}
          <div className="flex-1 flex items-center gap-4">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider w-10 text-right">
              0%
            </span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              disabled={preparing}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black"
            />
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider w-10">
              100%
            </span>
            <span className="text-sm font-medium text-gray-800 w-12 text-right">
              {quality}%
            </span>
          </div>

          <button
            onClick={handleDownload}
            disabled={preparing}
            className="bg-black text-white text-sm px-8 py-2.5 rounded font-medium tracking-wide disabled:opacity-50 min-w-[160px]"
          >
            {preparing ? "Preparing PDF…" : "Download PDF"}
          </button>
        </div>

        {/* Compression Progress Bar */}
        {preparing && progress.total > 0 && (
          <div className="w-full flex items-center gap-3">
            <span className="text-xs text-gray-500 font-medium">
              Compressing images...
            </span>
            <div className="flex-1 h-2 bg-gray-200 rounded overflow-hidden">
              <div
                className="h-full bg-black transition-all duration-300"
                style={{
                  width: `${(progress.current / progress.total) * 100}%`,
                }}
              />
            </div>
            <span className="text-xs text-gray-500 font-medium min-w-[60px] text-right">
              {progress.current} / {progress.total}
            </span>
          </div>
        )}
      </div>

      {/* PRINT CSS */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @page { size: A4 portrait; margin: 0; }
            @media print {
              html, body {
                width: 210mm;
                margin: 0;
                padding: 0;
                height: auto !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              body { zoom: 100%; }
              .print-page {
                width: 210mm !important;
                min-height: 297mm !important;
                page-break-after: always;
                break-after: page;
                page-break-inside: avoid;
                break-inside: avoid;
                margin: 0 !important;
              }
              .print-page:last-of-type {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              .shadow-md { box-shadow: none !important; }
            }
          `,
        }}
      />

      {/* PAGE 1: COVER */}
      <div className="print-page bg-[#f8f7f6] shadow-md w-[210mm] min-h-[297mm] relative overflow-hidden">
        <div className="absolute top-0 right-[27.7mm] w-[1px] h-[36mm] bg-gray-400"></div>
        <div className="absolute top-0 right-[21mm] w-[1px] h-[49.5mm] bg-gray-400"></div>

        <div className="absolute top-[9mm] left-1/2 -translate-x-1/2">
          <img
            src={STATIC.logo}
            alt="Zorba Stays"
            className="h-[31mm] w-auto object-contain"
          />
        </div>

        <h2 className="absolute top-[75mm] left-0 w-full text-center text-[15mm] leading-none PlayfairDisplay text-gray-800 tracking-wide">
          {name}
        </h2>

        <p className="absolute top-[92.5mm] left-0 w-full text-center text-[4.2mm] leading-none PlayfairDisplay text-gray-800 tracking-[0.18em] uppercase">
          {locationLine}
        </p>

        <p className="absolute top-[120mm] right-[20.7mm] montserrat text-[2.5mm] leading-none tracking-[0.28em] text-gray-600 uppercase">
          {coverText}
        </p>

        <div className="absolute left-[20.7mm] top-[125.3mm] w-[168.6mm] h-[135.5mm] overflow-hidden">
          {frontImage && <Photo src={frontImage} alt={name} />}
        </div>

        <div className="absolute bottom-[4.5mm] left-0 w-full flex justify-center">
          <div className="montserrat flex justify-center items-center space-x-4 text-[2.7mm] text-gray-500 tracking-[0.12em] font-medium">
            <a
              href="https://zorbastays.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{contact.website}</span>
            </a>
            <span className="h-[3mm] w-[1px] bg-gray-400"></span>
            <a href={`mailto:${contact.email}`}>
              <span>{contact.email}</span>
            </a>
            <span className="h-[3mm] w-[1px] bg-gray-400"></span>
            <span>{contact.phones}</span>
          </div>
        </div>
      </div>

      {/* PAGE 2: AMENITIES */}
      {amenityPages.map((amenityGroup, pageIndex, allPages) => (
        <div
          key={pageIndex}
          className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden"
        >
          <div className="absolute top-[25mm] left-[24mm]">
            <h1
              className="PlayfairDisplay text-gray-800 leading-none"
              style={{
                fontSize: "14mm",
                fontWeight: 400,
                letterSpacing: "-0.03em",
              }}
            >
              Amenities
            </h1>
            <p
              className="montserrat text-gray-700 uppercase"
              style={{
                marginTop: "4mm",
                fontSize: "2.4mm",
                letterSpacing: "0.18em",
                fontWeight: 500,
              }}
            >
              {name} - {locationLine}
            </p>
          </div>

          <div
            className="absolute left-[20mm] right-[20mm] top-[92mm]"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              columnGap: "7mm",
              rowGap: "15mm",
            }}
          >
            {amenityGroup.map((item, index) => (
              <div
                key={index}
                className="flex flex-col items-center justify-start text-center"
                style={{ height: "25mm" }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{ width: "17mm", height: "17mm" }}
                >
                  <img
                    src={item.imgSrc}
                    alt={item.name}
                    className="object-contain"
                    style={{
                      width: "100%",
                      height: "100%",
                      filter: "grayscale(1) brightness(0.35)",
                    }}
                  />
                </div>
                <div
                  className="montserrat uppercase text-gray-800"
                  style={{
                    marginTop: "4mm",
                    fontSize: "1.75mm",
                    lineHeight: "1.45",
                    letterSpacing: "0.16em",
                    fontWeight: 500,
                    maxWidth: "38mm",
                  }}
                >
                  {item.name}
                </div>
              </div>
            ))}
          </div>

          {pageIndex === allPages.length - 1 && (
            <div
              className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center"
              style={{ bottom: "12mm", width: "42mm", gap: "3mm" }}
            >
              <div
                style={{
                  width: "14mm",
                  height: "0.25mm",
                  backgroundColor: "#374151",
                }}
              />
              <div
                className="flex items-center justify-center"
                style={{
                  width: "7mm",
                  height: "7mm",
                  border: "0.25mm solid #9ca3af",
                  borderRadius: "50%",
                }}
              >
                <img
                  src={STATIC.amenitiesLogo}
                  alt="logo"
                  style={{
                    width: "4mm",
                    height: "4mm",
                    objectFit: "contain",
                    filter: "grayscale(1) brightness(0.35)",
                  }}
                />
              </div>
              <div
                style={{
                  width: "14mm",
                  height: "0.25mm",
                  backgroundColor: "#374151",
                }}
              />
            </div>
          )}
        </div>
      ))}

      {/* PAGE 3: THE VILLA */}
      <div className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden">
        <div className="absolute left-[20mm] top-[13mm]">
          <div className="flex items-baseline leading-none">
            <span
              className="CormorantGaramond"
              style={{ fontSize: "13.5mm", fontWeight: 600 }}
            >
              The
            </span>
            <span
              className="CormorantItalic text-gray-800 ml-[2mm]"
              style={{ fontSize: "13.5mm", fontWeight: 600 }}
            >
              {propertytype ? propertytype : "villa"}
            </span>
          </div>
          <p
            className="montserrat text-gray-700 uppercase"
            style={{
              marginTop: "4mm",
              fontSize: "2.4mm",
              letterSpacing: "0.18em",
              fontWeight: 500,
            }}
          >
            {locationLine}
          </p>
        </div>

        <div
          className="absolute overflow-hidden"
          style={{
            left: "20mm",
            top: "52mm",
            width: "170mm",
            height: "127.5mm",
          }}
        >
          {villaImage && <Photo src={villaImage} alt="Villa" />}
        </div>

        <div
          className="absolute montserrat text-gray-800 brochure-description"
          style={{
            left: "20mm",
            top: "185mm",
            width: "170mm",
            boxSizing: "border-box",
            fontSize: "3.9mm",
            lineHeight: "1.55",
            wordSpacing: "normal",
            letterSpacing: "normal",
            textAlign: "left",
          }}
        >
          <div
            className="brochure-content"
            dangerouslySetInnerHTML={{
              __html: renderContentHtml(content),
            }}
          />
        </div>
      </div>

      {/* PAGE 4: THE WAY WE CURATE (static) */}
      <div className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden">
        <div className="absolute left-[20mm] top-[15mm]">
          <h1
            className="CormorantGaramond leading-none"
            style={{
              fontSize: "13.5mm",
              fontWeight: 600,
              letterSpacing: "-0.03em",
            }}
          >
            The way <span className="CormorantItalic">we curate.</span>
          </h1>
        </div>

        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 210 297"
          preserveAspectRatio="none"
        >
          <path
            d="M 67 42 C 105 43, 163 48, 185 64 C 202 76, 194 91, 177 99 C 158 108, 129 109, 108 113 C 81 118, 58 128, 52 141 C 46 151, 57 158, 72 163 C 94 171, 126 176, 145 187 C 159 195, 164 204, 157 214"
            fill="none"
            stroke="#d1d5d6"
            strokeWidth="0.7"
            strokeLinecap="round"
          />
          <circle cx="67" cy="42" r="2.3" fill="#d1d5d6" />
          <circle cx="189" cy="68" r="2.3" fill="#d1d5d6" />
          <circle cx="113" cy="112" r="2.3" fill="#d1d5d6" />
          <circle cx="71" cy="163" r="2.3" fill="#d1d5d6" />
          <circle cx="157" cy="214" r="2.3" fill="#d1d5d6" />
        </svg>

        {STATIC.curate.map((c, i) => (
          <div
            key={i}
            className="absolute"
            style={{ left: c.left, top: c.top, width: c.width }}
          >
            <h2
              className="PlayfairDisplay text-gray-800"
              style={{ fontSize: "5.2mm", lineHeight: "1.05", fontWeight: 400 }}
            >
              {c.title.map((t, ti) => (
                <React.Fragment key={ti}>
                  {ti > 0 && <br />}
                  {t}
                </React.Fragment>
              ))}
            </h2>
            <p
              className="montserrat text-gray-700"
              style={{
                marginTop: c.mt,
                fontSize: "3.25mm",
                lineHeight: "1.45",
                letterSpacing: "0.12em",
              }}
            >
              {c.lines.map((l, li) => (
                <React.Fragment key={li}>
                  {li > 0 && <br />}
                  {l}
                </React.Fragment>
              ))}
            </p>
          </div>
        ))}
      </div>

      {/* DYNAMIC SECTION: one block per category + Photo Gallery */}
      <ReusableGalleryLayout sections={sections} />

      {/* WHY TRUST US */}
      <div className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden flex flex-col justify-start px-[22mm] pt-[20mm]">
        <h1 className="PlayfairDisplay leading-none text-[13mm] font-normal tracking-[-0.02em]">
          {STATIC.whyTrustUs.title}
        </h1>

        <h2 className="montserrat font-extrabold mt-[8mm] text-[4.8mm] leading-[1.25] tracking-[-0.01em] max-w-[140mm]">
          {STATIC.whyTrustUs.headingLine1}
          <br />
          {STATIC.whyTrustUs.headingLine2}
        </h2>

        <p className="montserrat mt-[6.5mm] text-[3.3mm] tracking-[0.12em] font-normal">
          {STATIC.whyTrustUs.highlight}
        </p>

        <p className="montserrat mt-[4.5mm] text-[3.3mm] leading-[1.5] tracking-[0.12em] max-w-[166mm]">
          {STATIC.whyTrustUs.paragraph1}
        </p>

        <p className="montserrat mt-[4.5mm] text-[3.3mm] leading-[1.5] tracking-[0.12em] max-w-[166mm]">
          {STATIC.whyTrustUs.paragraph2Part1}
          <strong className="font-extrabold">
            {STATIC.whyTrustUs.propertiesCount}
          </strong>
          {STATIC.whyTrustUs.paragraph2Part2}
        </p>

        <div className="grid grid-cols-2 mt-[16mm] gap-x-[14mm] gap-y-[12mm]">
          {STATIC.whyTrustUs.features.map((feature, idx) => {
            const IconComponent = feature.icon;
            return (
              <div key={idx} className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-0.5 text-[#c98a72] text-[35px]">
                  <IconComponent />
                </div>
                <div>
                  <h3 className="montserrat font-extrabold text-[4.2mm] tracking-[0.03em]">
                    {feature.title}
                  </h3>
                  <p className="montserrat mt-[2.5mm] text-[3.20mm] leading-[1.5] tracking-[0.10em]">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* OUR TRUSTED PARTNERS */}
      <div className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden flex flex-col items-center pt-[22mm] px-[20mm]">
        <h2 className="montserrat font-bold text-gray-800 text-[6.5mm] tracking-tight text-center">
          {STATIC.trustedPartners.title}
        </h2>

        <div className="w-full h-[0.3mm] bg-[#dcdcdc] mt-[8mm]" />

        <div className="w-full grid grid-cols-3 relative py-[8mm] gap-y-[8mm]">
          <div className="absolute top-0 bottom-0 left-1/3 w-[0.3mm] bg-[#dcdcdc] -translate-x-1/2" />
          <div className="absolute top-0 bottom-0 left-2/3 w-[0.3mm] bg-[#dcdcdc] -translate-x-1/2" />

          {STATIC.trustedPartners.items.map((partner, index) => (
            <div
              key={index}
              className="flex items-center justify-center px-[8mm] py-[3mm] h-[36mm]"
            >
              <img
                src={partner.logo}
                alt={partner.name}
                className="max-h-[33mm] max-w-[44mm] w-auto h-auto object-contain"
              />
            </div>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <div className="print-page w-[210mm] min-h-[297mm] px-12 pt-20 flex flex-col relative bg-[#f8f7f6] shadow-md">
        {[STATIC.services.section1, STATIC.services.section2].map((s, i) => {
          // Section 1 → Travel Guide (destination-based, static)
          // Section 2 → Concierge (DB first → destination → global fallback)
          let serviceLink;
          if (i === 0) {
            serviceLink =
              STATIC.travelGuideLinks[propDestinationKey] ||
              STATIC.services.link;
          } else {
            serviceLink =
              String(conserlink || "").trim() ||
              STATIC.conciergeLinks[propDestinationKey] ||
              STATIC.services.link;
          }

          return (
            <div
              key={i}
              className={`max-w-[92%] ml-5 ${i === 0 ? "mb-12" : "mt-4"}`}
            >
              <h2 className="text-[44px] CormorantGaramond font-bold text-gray-900 mb-6">
                {s.first}{" "}
                <span className="CormorantItalic font-bold">{s.italic}</span>
              </h2>

              <p
                className="text-gray-700 montserrat text-[14px] tracking-wide text-justify mb-8 leading-relaxed"
                style={{ wordSpacing: "1px" }}
              >
                {s.description}
              </p>

              <div
                className={`relative w-full h-36 flex flex-col items-center justify-center overflow-hidden rounded-sm bg-cover ${
                  i === 0 ? "bg-center" : "bg-top"
                }`}
                style={{ backgroundImage: STATIC.services.bannerBg }}
              >
                <div className="absolute inset-0 bg-[#d4dccf] opacity-85"></div>
                <h3 className="relative z-10 text-2xl font-black text-gray-900 mb-3 tracking-tight">
                  {s.bannerText}
                </h3>
                <a href={serviceLink} target="_blank" rel="noopener noreferrer">
                  <button className="relative z-10 bg-black text-white text-[9px] font-semibold tracking-widest px-7 py-2 uppercase">
                    Click Here
                  </button>
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* ABOUT ZORBA */}
      <div className="print-page w-[210mm] min-h-[297mm] relative flex flex-col justify-between px-16 pt-20 pb-12 overflow-hidden bg-[#fbfbfa] text-[#2c3333]">
        <div className="flex flex-col">
          <h1 className="text-[50px] font-bold leading-tight mb-80">
            <span className="CormorantGaramond lowercase text-[#1e272e] tracking-tight">
              {STATIC.aboutZorba.titlePrefix}{" "}
            </span>
            <span className="CormorantItalic text-[#1e272e]">
              {STATIC.aboutZorba.titleSuffix}
            </span>
          </h1>

          <h2 className="text-[34px] font-bold leading-snug mb-8">
            <span className="CormorantGaramond text-[#1e272e]">
              {STATIC.aboutZorba.headingPrefix}{" "}
            </span>
            <span className="CormorantItalic text-[#1e272e]">
              {STATIC.aboutZorba.headingSuffix}
            </span>
          </h2>

          <div className="montserrat flex flex-col space-y-6 text-[13.5px] leading-[1.8] tracking-[0.05em] text-[#333d42] pr-6">
            {STATIC.aboutZorba.paragraphs.map((para, index) => (
              <p key={index}>{para}</p>
            ))}
          </div>
        </div>

        <div className="w-full flex justify-center pt-8">
          <a
            href={`https://${contact.website}`}
            target="_blank"
            rel="noreferrer"
            className="text-[13px] tracking-widest text-[#2c3333] border-b border-[#a86558] pb-[1px] hover:opacity-80 transition-opacity"
          >
            {contact.website}
          </a>
        </div>
      </div>

      {/* BACK COVER */}
      <div className="print-page w-[210mm] min-h-[297mm] relative flex flex-col justify-between items-stretch px-16 pt-20 pb-10 bg-[#fbfbfa] text-[#1e272e]">
        <div className="flex flex-col">
          <h2 className="CormorantGaramond font-bold text-[44px] leading-[1.25] tracking-tight text-[#1e272e]">
            <div>
              <span>Indulge in </span>
              <span className="CormorantItalic">the extraordinary;</span>
            </div>
            <div>
              <span>life's too </span>
              <span className="CormorantItalic">short for ordinary</span>
            </div>
          </h2>
        </div>

        <div className="flex justify-center items-center my-auto">
          <img
            src={STATIC.backCover.logo}
            alt="Zorba Stays"
            className="w-56 h-auto object-contain brightness-0"
          />
        </div>

        <div className="w-full flex justify-center items-center space-x-2 text-[11px] tracking-[0.12em] text-[#333d42] font-sans">
          <a
            href={`https://${contact.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-75 transition-opacity"
          >
            {contact.website}
          </a>
          <span>|</span>
          <a
            href={`mailto:${contact.email}`}
            className="hover:opacity-75 transition-opacity"
          >
            {contact.email}
          </a>
          <span>|</span>
          <span>{contact.phones}</span>
        </div>
      </div>
    </div>
  );
};

export default NewQuickBrochure;

// import React, { useEffect, useMemo, useState } from "react";
// import { useParams, useSearchParams } from "react-router-dom";
// import { PiEarLight, PiHandshakeLight } from "react-icons/pi";
// import { MdOutlineRoomService } from "react-icons/md";
// import { IoDiamondOutline } from "react-icons/io5";
// import DOMPurify from "dompurify";
// import "../Dashboard/Dashboard.css";

// /* -------------------------------------------------------------------------- */
// /*  CONFIG                                                                    */
// /* -------------------------------------------------------------------------- */

// const API_BASE = "https://dash.zorbastays.com/web-backend";

// // If your admin API needs a token, return the headers here.
// // e.g. return { Authorization: `Bearer ${localStorage.getItem("token")}` };
// const getAuthHeaders = () => ({});

// /* -------------------------------------------------------------------------- */
// /*  STATIC CONTENT (never changes per property)                               */
// /* -------------------------------------------------------------------------- */

// const STATIC = {
//   logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9fc19416-6ee2-42fd-bc5a-4f0b5dc29800/public",
//   // coverText: "Explore a luxury villa.",
//   amenitiesLogo: "https://dash.zorbastays.com/assets/zorba_logo.webp",
//   contact: {
//     website: "www.zorbastays.com",
//     email: "info@zorbastays.com",
//     phones: "63552-31066, 98750-28066",
//   },

//   curate: [
//     {
//       left: "27mm",
//       top: "48mm",
//       width: "58mm",
//       mt: "3mm",
//       title: ["We Choose with Care"],
//       lines: [
//         "Every Zorba stay is",
//         "personally verified for its",
//         "design, service, and sense",
//         "of place.",
//       ],
//     },
//     {
//       left: "114mm",
//       top: "66mm",
//       width: "63mm",
//       mt: "3mm",
//       title: ["We Understand Your", "“Why” Before the “Where”"],
//       lines: [
//         "Every journey begins with",
//         "intent. We craft stays that",
//         "reflect your purpose, your",
//         "people, and the memories",
//         "you seek to create.",
//       ],
//     },
//     {
//       left: "103mm",
//       top: "136mm",
//       width: "63mm",
//       mt: "4mm",
//       title: ["We’re Your Constant", "Point of Support"],
//       lines: [
//         "From the first conversation",
//         "to your final day, our team",
//         "ensures every detail flows",
//         "seamlessly.",
//       ],
//     },
//     {
//       left: "23mm",
//       top: "174mm",
//       width: "62mm",
//       mt: "4mm",
//       title: ["We Bring Access, Not", "Aggregation"],
//       lines: [
//         "Our collection opens doors",
//         "to stays and experiences",
//         "you won’t find online,",
//         "offered through trust and",
//         "discovery.",
//       ],
//     },
//     {
//       left: "109mm",
//       top: "215mm",
//       width: "65mm",
//       mt: "4mm",
//       title: ["Beyond Travel —", "We Stay in Touch"],
//       lines: [
//         "We stay connected beyond",
//         "your travels - inspiring your",
//         "next journey with new",
//         "destinations and",
//         "discoveries.",
//       ],
//     },
//   ],

//   whyTrustUs: {
//     title: "Why Trust Us?",
//     headingLine1: "Because your holiday deserves",
//     headingLine2: "more than a Booking.",
//     highlight:
//       "Handpicked stays. Verified details. Trusted partners. Personal support.",
//     paragraph1:
//       "At Zorba Stays, we go beyond finding a place to stay. We curate villas, boutique hotels and unique stays across India, focusing on comfort, privacy and experiences that matter.",
//     paragraph2Part1: "With ",
//     propertiesCount: "3,100+ properties",
//     paragraph2Part2:
//       ", our curated collection helps you spend less time searching and more time enjoying.",
//     features: [
//       {
//         title: "The Art of Listening",
//         description:
//           "At the heart of what Zorba Stays do is the art of listening, truly understanding what moves you, excites you and helps you feel at home in the world.",
//         icon: PiEarLight,
//       },
//       {
//         title: "We Serve With Intention",
//         description:
//           "Every detail is handled with care. Your preferences are remembered, your time is respected, your comfort is well taken care of.",
//         icon: MdOutlineRoomService,
//       },
//       {
//         title: "We Value Access Over Excess",
//         description:
//           "True luxury doesn't just mean extravagance. It's about access to inaccessible and extraordinary experiences that cannot be found in random bucket lists.",
//         icon: PiHandshakeLight,
//       },
//       {
//         title: "We Take Full Responsibility",
//         description:
//           "We commit to your experiences as if it were our own. We stay involved from your first hello to the moment you land home, to ensure your journey feels seamless, personal and deeply cared for.",
//         icon: IoDiamondOutline,
//       },
//     ],
//   },

//   trustedPartners: {
//     title: "Our Trusted Partners",
//     items: [
//       {
//         name: "The Postcard Hotel",
//         logo: "https://dax6u9k4peaex.cloudfront.net//assets/images/logobonw.webp",
//       },
//       {
//         name: "stay vista",
//         logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/d291d436-8c41-4a33-e263-c18f5812d500/public",
//       },
//       {
//         name: "Tree of life",
//         logo: "https://encrypted-tbn3.gstatic.com/images?q=tbn:ANd9GcQrqStB4M3KHLiyctgsqavUhVdZHhbjtRY_CK3PE4fHOU2M7RLT",
//       },
//       {
//         name: "RAINFOREST",
//         logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/5ae4f033-d4c1-454d-6c89-096f68d95600/public",
//       },
//       {
//         name: "ITC Hotel",
//         logo: "https://d2q79iu7y748jz.cloudfront.net/s/_squarelogo/256x256/44093aec8ea005db179a34c1ce232c6f",
//       },
//       {
//         name: "Lohono stays",
//         logo: "https://gos3.ibcdn.com/c1254793-5809-4f76-912f-2463357355ab.jpg",
//       },
//       {
//         name: "hireavilla",
//         logo: "https://www.hireavilla.in/images/color-logo-2.svg",
//       },
//       {
//         name: "elivaas",
//         logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/c24b0b33-3992-45fd-c13f-ed7cba096c00/public",
//       },
//       {
//         name: "saffronstays",
//         logo: "https://www.travelturtle.world/wp-content/uploads/2026/08/Horizontal_JPG_TaglineLogo-1920x873.jpeg",
//       },
//       {
//         name: "walden",
//         logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/b248ffa7-be21-4660-c744-5afbd5e10300/public",
//       },
//       {
//         name: "Dolkhar",
//         logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRC7Qy2cnCwNgnX4CoAcGhHPv1LxEieJ_g0vd3LU157tZjMufSn",
//       },
//       {
//         name: "kavish",
//         logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/357996d4-a723-46cd-f638-072cbf322f00/public",
//       },
//       {
//         name: "Coco Shambhala",
//         logo: "https://media.licdn.com/dms/image/v2/C4E0BAQFSahOYLtRjgg/company-logo_200_200/company-logo_200_200/0/1631339580135?e=2147483647&v=beta&t=wvjJY2rvMEwkXuRYl5N3-FpPcX5XG3bo1Wuke0c9Fy8",
//       },
//       {
//         name: "Acacia villas",
//         logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/e59df9f1-83da-4e41-849f-147b567c7c00/public",
//       },
//       {
//         name: "WelcomHeritage",
//         logo: "https://www.shivavilaspalacehotel.com/assets/img/right-logo.png",
//       },
//     ],
//   },

//   services: {
//     bannerBg:
//       "url('https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/6bbede15-8c21-42b1-d91c-cc2fde9f4a00/public')",
//     link: "https://drive.google.com/file/d/1jOhgd_mzOigpKwZ9ZCWaVt_iyguQlFKa/view",
//     section1: {
//       first: "Feel every",
//       italic: "destination",
//       description:
//         "Go beyond the stay and experience the destination like a local. From curated places to eat and explore to hidden gems worth discovering, our guides bring you closer to the culture, landscapes, and character of every destination.",
//       bannerText: "Check Travel Guide for Your Stay",
//     },
//     section2: {
//       first: "Concierge at",
//       italic: "your service",
//       description:
//         "Travel, made effortless. From local experiences and private transfers to dining and special celebrations, our concierge team takes care of the details — thoughtfully, seamlessly, and with a touch of Zorba charm.",
//       bannerText: "Explore Concierge Services",
//     },
//   },
//   travelGuideLinks: {
//     goa: "https://drive.google.com/file/d/1jOhgd_mzOigpKwZ9ZCWaVt_iyguQlFKa/view?usp=share_link",
//     maharashtra:
//       "https://drive.google.com/file/d/1aMx2ag3PxXv5crDuaZJowa00B6xXeafx/view?usp=sharing",
//     rajasthan:
//       "https://drive.google.com/file/d/1L40R5H7bLE6oRv77_IxEZMp5wsAQMoWB/view?usp=share_link",
//   },

//   conciergeLinks: {
//     goa: "https://drive.google.com/file/d/1oRimikt1qiHWq2X9K2vPmqJ_YUYI3FEI/view?usp=sharing",
//     maharashtra:
//       "https://drive.google.com/file/d/12F7eF8qNIi0AjmYj8aK67Lbh3k0ywJZV/view?usp=sharing",
//     rajasthan:
//       "https://drive.google.com/file/d/1jh84Z0DRoi0Ii77fhh6vvdMJlTqwpdfu/view?usp=sharing",
//   },
//   aboutZorba: {
//     titlePrefix: "about",
//     titleSuffix: "Zorba Stays",
//     headingPrefix: "Find your self",
//     headingSuffix: "in nature.",
//     paragraphs: [
//       "Zorba was born from the slow realization that everyone we've assisted or known over the years, have all wanted very different kinds of travel experiences.",
//       "But even with all the differences, what anchored them together was the singular belief in the human & nature connection, a belief that there can be very few pleasures as universal and as pure as the simple feeling of sand between your toes on a beach or the sensation of walking barefoot on fresh green grass.",
//       "This has become our attempt with Zorba Stays, to assist people in having an honest beautiful experience that they've always wanted.",
//       "Zorba Stays is our hope to assist people in exploring the beautiful landscapes of our country, from the warm beaches of Goa to the serene valleys of Uttarakhand and beyond. Whether someone is looking for meditation or a collective celebration- a marriage or a party, we want to invite everyone, as long as they're seeking an honest, beautiful experience. Come explore with us and realize this dream together at Zorba Stays.",
//     ],
//   },

//   backCover: {
//     logo: "https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/d4289f22-91de-411f-d677-6e92f843ea00/public",
//   },
// };
// /* -------------------------------------------------------------------------- */
// /*  IMAGE SIZE / COMPRESSION                                                  */
// /* -------------------------------------------------------------------------- */
// /* -------------------------------------------------------------------------- */
// /*  IMAGE SIZE / COMPRESSION                                                  */
// /* -------------------------------------------------------------------------- */

// const QUALITY_PRESETS = {
//   low: { maxW: 800, q: 55 },
//   medium: { maxW: 1100, q: 70 },
//   high: { maxW: 1600, q: 82 },
// };
// const safeCompressImage = (src, quality) =>
//   new Promise((resolve) => {
//     if (!src || !src.startsWith("http")) return resolve(src);

//     const img = new Image();
//     img.crossOrigin = "anonymous";

//     img.onload = () => {
//       try {
//         const maxW = 1600;
//         const scale = Math.min(1, maxW / img.naturalWidth);
//         const w = Math.round(img.naturalWidth * scale);
//         const h = Math.round(img.naturalHeight * scale);

//         const canvas = document.createElement("canvas");
//         canvas.width = w;
//         canvas.height = h;
//         const ctx = canvas.getContext("2d");

//         // Safety net: fill with page colour so any transparency isn't black
//         ctx.fillStyle = "#f8f7f6";
//         ctx.fillRect(0, 0, w, h);
//         ctx.drawImage(img, 0, 0, w, h);

//         const jpegQuality = Math.max(0.05, quality / 100);
//         resolve(canvas.toDataURL("image/jpeg", jpegQuality)); // throws if CORS-tainted
//       } catch {
//         resolve(src);
//       }
//     };

//     img.onerror = () => resolve(src);
//     img.src = src;
//   });
// /* -------------------------------------------------------------------------- */
// /*  HELPERS                                                                   */
// /* -------------------------------------------------------------------------- */

// const isHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

// const renderContentHtml = (raw) => {
//   if (!raw) return "";
//   if (!isHtml(raw)) {
//     const esc = String(raw)
//       .replace(/&/g, "&amp;")
//       .replace(/</g, "&lt;")
//       .replace(/>/g, "&gt;");
//     return `<p>${esc.replace(/\n/g, "<br>")}</p>`;
//   }

//   const clean = DOMPurify.sanitize(raw, {
//     ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u"],
//     ALLOWED_ATTR: [],
//   });

//   return (
//     clean
//       // non-breaking spaces -> normal spaces so lines can wrap
//       .replace(/&nbsp;|\u00a0/g, " ")
//       // "five-<br>bedroom" -> "five-bedroom"
//       .replace(/-\s*<br\s*\/?>\s*/gi, "-")
//       // a <br> in the middle of a sentence -> a space
//       // (a <br> after . ! ? : ; or a quote is kept as a real line break)
//       .replace(/([^.!?:;"”’>])\s*<br\s*\/?>\s*/gi, "$1 ")
//   );
// };

// const norm = (v) => (v == null ? "" : String(v).trim().toLowerCase());
// const coverTextFor = (propertytype) => {
//   const cleaned = String(propertytype || "")
//     .trim()
//     .replace(/^\d+\s*(bhk|bedroom|bed|br)\b[\s-]*/i, "")
//     .trim()
//     .toLowerCase();

//   return `Explore a luxury ${cleaned || "villa"}.`;
// };
// const destinationKey = (raw) => {
//   const s = norm(raw);
//   if (!s) return "";
//   if (s.includes("goa")) return "goa";
//   if (
//     s.includes("maharashtra") ||
//     s.includes("mumbai") ||
//     s.includes("lonavala") ||
//     s.includes("alibaug") ||
//     s.includes("karjat") ||
//     s.includes("panchgani") ||
//     s.includes("mahabaleshwar") ||
//     /\bmaha\b/.test(s)
//   )
//     return "maharashtra";
//   if (s.includes("rajasthan") || s.includes("jaipur") || s.includes("udaipur"))
//     return "rajasthan";
//   return "";
// };
// const isGalleryCategory = (cat) => {
//   const c = norm(cat);
//   return (
//     !c || c === "n/a" || c === "na" || c === "null" || c === "additional photos"
//   );
// };

// const isCoverCategory = (cat) => norm(cat) === "cover photo";

// const isBedBath = (cat) =>
//   /^(bedroom|bathroom)\b/i.test(String(cat || "").trim());

// // "Living Room" -> { first: "Living", second: "Room" }; "Pool" -> { first: "", second: "Pool" }
// const splitTitle = (cat) => {
//   const words = String(cat).trim().split(/\s+/);
//   if (words.length === 1) return { first: "", second: words[0] };
//   return {
//     first: words.slice(0, -1).join(" "),
//     second: words[words.length - 1],
//   };
// };

// const titleFontSize = (text) => {
//   const len = text.length;
//   if (len > 24) return "8mm";
//   if (len > 16) return "10.5mm";
//   return "13.5mm";
// };

// /** Turns the raw property + amenities responses into everything the pages need. */
// const buildBrochureData = (property, amenitiesList) => {
//   const images = [...(property.images || [])]
//     .filter((i) => i && i.imageurl)
//     .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

//   const locationLine = [property.location, property.destination]
//     .filter(Boolean)
//     .join(", ");

//   // Cover images
//   const covers = images.filter((i) => isCoverCategory(i.category));
//   const frontImage = covers[0]?.imageurl || images[0]?.imageurl || "";
//   const villaImage =
//     covers[1]?.imageurl ||
//     images.find((i) => i.imageurl !== frontImage)?.imageurl ||
//     "";

//   // Amenities (keep the order of the addons string)
//   const amenityMap = new Map(
//     (amenitiesList || []).map((a) => [String(a.id), a]),
//   );
//   const amenities = String(property.addons || "")
//     .split(",")
//     .map((s) => s.trim())
//     .filter(Boolean)
//     .map((id) => amenityMap.get(id))
//     .filter(Boolean)
//     .map((a) => ({ name: a.name, imgSrc: a.url }));

//   // Category sections + gallery
//   const groups = new Map(); // key -> { label, images[] }
//   const gallery = [];

//   images.forEach((img) => {
//     if (isCoverCategory(img.category)) return; // cover photos live on page 1 / 3
//     if (isGalleryCategory(img.category)) {
//       gallery.push(img.imageurl);
//       return;
//     }
//     const label = String(img.category).trim();
//     const key = norm(label);
//     if (!groups.has(key)) groups.set(key, { label, images: [] });
//     groups.get(key).images.push(img.imageurl);
//   });

//   // Page order: Pool, Living, Kitchen, Dining, (any other categories),
//   // Bedroom 1..n, Bathroom 1..n, Exterior, Jacuzzi, Courtyard, Games,
//   // Theatre, Gym, Garden, then the Photo Gallery (added below)
//   const AFTER_BATHROOMS = [
//     "exterior",
//     "jacuzzi",
//     "courtyard",
//     "games",
//     "theatre",
//     "gym",
//     "garden",
//   ];
//   const categoryRank = (label) => {
//     const l = norm(label);
//     if (l.startsWith("pool")) return 1;
//     if (l.startsWith("living")) return 2;
//     if (l.startsWith("kitchen")) return 3;
//     if (l.startsWith("dining")) return 4;
//     if (l.startsWith("bedroom")) return 6;
//     if (l.startsWith("bathroom")) return 7;
//     const idx = AFTER_BATHROOMS.findIndex((k) => l.startsWith(k));
//     if (idx !== -1) return 8 + idx / 100;
//     return 5; // any other category — after dining, before bedrooms
//   };
//   const trailingNumber = (label) => {
//     const m = String(label).match(/(\d+)\s*$/);
//     return m ? parseInt(m[1], 10) : 0;
//   };

//   const orderedGroups = [...groups.values()].sort((a, b) => {
//     const rankDiff = categoryRank(a.label) - categoryRank(b.label);
//     if (rankDiff !== 0) return rankDiff;
//     // Bedroom 2 before Bedroom 10
//     return trailingNumber(a.label) - trailingNumber(b.label);
//   });

//   const sections = [];
//   orderedGroups.forEach((g, idx) => {
//     if (isBedBath(g.label)) {
//       sections.push({
//         key: `cat-${idx}`,
//         variant: "bedroom",
//         title: g.label.toUpperCase(),
//         text: locationLine,
//         subtitle: locationLine,
//         images: g.images,
//       });
//     } else {
//       const { first, second } = splitTitle(g.label);
//       sections.push({
//         key: `cat-${idx}`,
//         titleFirst: first,
//         titleSecond: second,
//         subtitle: locationLine,
//         images: g.images,
//       });
//     }
//   });

//   if (gallery.length) {
//     sections.push({
//       key: "photo-gallery",
//       variant: "photoGallery",
//       isFirstPhotoGalleryGroup: true,
//       subtitle: locationLine,
//       images: gallery,
//     });
//   }

//   return {
//     name: property.propertyname || "",
//     locationLine,
//     content: property.content || "",
//     propertytype: property.propertytype || "",
//     conserlink: property.conserlink || "",
//     destinationKey:
//       destinationKey(property.destination) || destinationKey(property.location),
//     frontImage,
//     villaImage,
//     amenities,
//     sections,
//   };
// };

// /* -------------------------------------------------------------------------- */
// /*  GALLERY LAYOUT (paginates a category's images into A4 pages)              */
// /* -------------------------------------------------------------------------- */

// const ReusableGalleryLayout = ({ sections, continuationOnly = false }) => {
//   return sections.map((section) => {
//     const galleryImages = section.images || [];
//     const pages = [];

//     if (galleryImages.length === 0) return null;

//     if (continuationOnly) {
//       let index = 0;
//       while (index < galleryImages.length) {
//         const remaining = galleryImages.length - index;
//         if (remaining >= 3) {
//           pages.push({
//             type: "three",
//             images: galleryImages.slice(index, index + 3),
//           });
//           index += 3;
//           if (index < galleryImages.length) {
//             const afterThree = galleryImages.length - index;
//             if (afterThree === 1 || afterThree === 4) {
//               pages.push({
//                 type: "singleContinuation",
//                 images: galleryImages.slice(index, index + 1),
//               });
//               index += 1;
//             }
//           }
//         } else if (remaining === 2) {
//           pages.push({
//             type: "two",
//             images: galleryImages.slice(index, index + 2),
//           });
//           index += 2;
//         } else {
//           pages.push({
//             type: "singleContinuation",
//             images: galleryImages.slice(index, index + 1),
//           });
//           index += 1;
//         }
//       }
//     } else if (galleryImages.length === 1) {
//       pages.push({ type: "singleFirst", images: galleryImages.slice(0, 1) });
//     } else if (galleryImages.length === 2) {
//       pages.push({ type: "twoFirst", images: galleryImages.slice(0, 2) });
//     } else if (galleryImages.length === 3) {
//       pages.push({ type: "threeFirst", images: galleryImages.slice(0, 3) });
//     } else {
//       let index = 0;
//       while (index < galleryImages.length) {
//         const remaining = galleryImages.length - index;
//         if (remaining >= 3) {
//           pages.push({
//             type: index === 0 ? "threeFirst" : "three",
//             images: galleryImages.slice(index, index + 3),
//           });
//           index += 3;
//           if (index < galleryImages.length) {
//             const afterThree = galleryImages.length - index;
//             if (afterThree === 1 || afterThree === 4) {
//               pages.push({
//                 type: "singleContinuation",
//                 images: galleryImages.slice(index, index + 1),
//               });
//               index += 1;
//             }
//           }
//         } else if (remaining === 2) {
//           pages.push({
//             type: index === 0 ? "twoFirst" : "two",
//             images: galleryImages.slice(index, index + 2),
//           });
//           index += 2;
//         } else {
//           pages.push({
//             type: index === 0 ? "singleFirst" : "singleContinuation",
//             images: galleryImages.slice(index, index + 1),
//           });
//           index += 1;
//         }
//       }
//     }

//     const alt = section.titleSecond || section.title || "Gallery";

//     return pages.map((page, pageIndex) => {
//       const hasBedroomTitle =
//         pageIndex === 0 && !continuationOnly && section.variant === "bedroom";

//       const hasStandardTitle =
//         pageIndex === 0 &&
//         !continuationOnly &&
//         section.variant !== "bedroom" &&
//         section.variant !== "photoGallery";

//       const isPhotoGalleryFirstPage =
//         section.variant === "photoGallery" &&
//         section.isFirstPhotoGalleryGroup &&
//         pageIndex === 0;

//       const isWithMainTitle =
//         hasBedroomTitle || hasStandardTitle || isPhotoGalleryFirstPage;

//       const standardFull =
//         `${section.titleFirst || ""} ${section.titleSecond || ""}`.trim();

//       return (
//         <div
//           key={`${section.key}-${pageIndex}`}
//           className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden"
//         >
//           {/* BEDROOM / BATHROOM TITLE */}
//           {hasBedroomTitle && (
//             <div className="absolute left-[20mm] top-[26mm] z-20">
//               <h2
//                 className="PlayfairDisplay text-gray-800 leading-none"
//                 style={{
//                   fontSize: "6.5mm",
//                   fontWeight: 600,
//                   letterSpacing: "-0.02em",
//                 }}
//               >
//                 {section.title}
//               </h2>
//               <p
//                 className="montserrat text-gray-700 uppercase"
//                 style={{
//                   marginTop: "3mm",
//                   fontSize: "2.4mm",
//                   letterSpacing: "0.18em",
//                   fontWeight: 500,
//                 }}
//               >
//                 {section.text}
//               </p>
//             </div>
//           )}

//           {/* STANDARD LEFT TITLE */}
//           {hasStandardTitle && (
//             <div className="absolute left-[20mm] top-[22mm] z-20">
//               <div className="flex items-baseline leading-none">
//                 {section.titleFirst && (
//                   <span
//                     className="CormorantGaramond"
//                     style={{
//                       fontSize: titleFontSize(standardFull),
//                       fontWeight: 600,
//                     }}
//                   >
//                     {section.titleFirst}
//                   </span>
//                 )}
//                 <span
//                   className="CormorantItalic"
//                   style={{
//                     fontSize: titleFontSize(standardFull),
//                     fontWeight: 600,
//                     marginLeft: section.titleFirst ? "2mm" : 0,
//                   }}
//                 >
//                   {section.titleSecond}
//                 </span>
//               </div>
//               <p
//                 className="montserrat text-gray-700 uppercase"
//                 style={{
//                   marginTop: "3.5mm",
//                   fontSize: "2.8mm",
//                   letterSpacing: "0.18em",
//                   fontWeight: 500,
//                 }}
//               >
//                 {section.subtitle}
//               </p>
//             </div>
//           )}

//           {/* PHOTO GALLERY (first page) */}
//           {isPhotoGalleryFirstPage && (
//             <div className="absolute left-[20mm] top-[22mm] z-20">
//               <div className="flex items-baseline leading-none">
//                 <span
//                   className="CormorantGaramond"
//                   style={{ fontSize: "13.5mm", fontWeight: 600 }}
//                 >
//                   Photo
//                 </span>
//                 <span
//                   className="CormorantItalic ml-[2mm]"
//                   style={{ fontSize: "13.5mm", fontWeight: 600 }}
//                 >
//                   gallery
//                 </span>
//               </div>
//               <p
//                 className="montserrat text-gray-700 uppercase"
//                 style={{
//                   marginTop: "3.5mm",
//                   fontSize: "2.8mm",
//                   letterSpacing: "0.18em",
//                   fontWeight: 500,
//                 }}
//               >
//                 {section.subtitle}
//               </p>
//             </div>
//           )}

//           {/* SINGLE IMAGE (FIRST PAGE) */}
//           {page.type === "singleFirst" && isWithMainTitle && (
//             <div
//               className="absolute overflow-hidden"
//               style={{
//                 left: "20mm",
//                 top: "54mm",
//                 width: "170mm",
//                 height: "221mm",
//               }}
//             >
//               <img
//                 src={page.images[0]}
//                 alt={alt}
//                 data-compress
//                 className="w-full h-full object-cover"
//               />
//             </div>
//           )}

//           {/* TWO IMAGES (WITH MAIN HEADING) */}
//           {page.type === "twoFirst" && isWithMainTitle && (
//             <div
//               className="absolute"
//               style={{
//                 left: "20mm",
//                 top: "54mm",
//                 width: "170mm",
//                 height: "221mm",
//               }}
//             >
//               <div
//                 className="absolute left-0 top-0 w-full overflow-hidden"
//                 style={{ height: "108.5mm" }}
//               >
//                 <img
//                   src={page.images[0]}
//                   alt={alt}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//               <div
//                 className="absolute left-0 w-full overflow-hidden"
//                 style={{ top: "112.5mm", height: "108.5mm" }}
//               >
//                 <img
//                   src={page.images[1]}
//                   alt={alt}
//                   data-compress
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//             </div>
//           )}

//           {/* THREE IMAGES (WITH MAIN HEADING) */}
//           {page.type === "threeFirst" && isWithMainTitle && (
//             <div
//               className="absolute"
//               style={{
//                 left: "20mm",
//                 top: "54mm",
//                 width: "170mm",
//                 height: "221mm",
//               }}
//             >
//               <div
//                 className="absolute left-0 top-0 w-full overflow-hidden"
//                 style={{ height: "120mm" }}
//               >
//                 <img
//                   src={page.images[0]}
//                   alt={alt}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//               <div
//                 className="absolute left-0 overflow-hidden"
//                 style={{ top: "124mm", width: "83mm", height: "97mm" }}
//               >
//                 {page.images[1] && (
//                   <img
//                     src={page.images[1]}
//                     alt={alt}
//                     className="w-full h-full object-cover"
//                   />
//                 )}
//               </div>
//               <div
//                 className="absolute right-0 overflow-hidden"
//                 style={{ top: "124mm", width: "83mm", height: "97mm" }}
//               >
//                 {page.images[2] && (
//                   <img
//                     src={page.images[2]}
//                     alt={alt}
//                     data-compress
//                     className="w-full h-full object-cover"
//                   />
//                 )}
//               </div>
//             </div>
//           )}

//           {/* THREE IMAGES */}
//           {(page.type === "three" ||
//             (page.type === "threeFirst" && !isWithMainTitle)) && (
//             <div
//               className="absolute"
//               style={{
//                 left: "20mm",
//                 top: "20mm",
//                 width: "170mm",
//                 height: "257mm",
//               }}
//             >
//               <div
//                 className="absolute left-0 top-0 w-full overflow-hidden"
//                 style={{ height: "140mm" }}
//               >
//                 <img
//                   src={page.images[0]}
//                   alt={alt}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//               <div
//                 className="absolute left-0 overflow-hidden"
//                 style={{ top: "144mm", width: "83mm", height: "113mm" }}
//               >
//                 <img
//                   src={page.images[1]}
//                   alt={alt}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//               <div
//                 className="absolute right-0 overflow-hidden"
//                 style={{ top: "144mm", width: "83mm", height: "113mm" }}
//               >
//                 <img
//                   src={page.images[2]}
//                   alt={alt}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//             </div>
//           )}

//           {/* TWO IMAGES */}
//           {(page.type === "two" ||
//             (page.type === "twoFirst" && !isWithMainTitle)) && (
//             <div
//               className="absolute"
//               style={{
//                 left: "20mm",
//                 top: "20mm",
//                 width: "170mm",
//                 height: "257mm",
//               }}
//             >
//               <div
//                 className="absolute left-0 top-0 w-full overflow-hidden"
//                 style={{ height: "126.5mm" }}
//               >
//                 <img
//                   src={page.images[0]}
//                   alt={alt}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//               <div
//                 className="absolute left-0 w-full overflow-hidden"
//                 style={{ top: "130.5mm", height: "126.5mm" }}
//               >
//                 <img
//                   src={page.images[1]}
//                   alt={alt}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//             </div>
//           )}

//           {/* SINGLE IMAGE */}
//           {page.type === "singleFirst" && !isWithMainTitle && (
//             <div
//               className="absolute overflow-hidden"
//               style={{
//                 left: "20mm",
//                 top: "20mm",
//                 width: "170mm",
//                 height: "257mm",
//               }}
//             >
//               <img
//                 src={page.images[0]}
//                 alt={alt}
//                 className="w-full h-full object-cover"
//               />
//             </div>
//           )}

//           {/* FULL BLEED CONTINUATION */}
//           {page.type === "singleContinuation" && (
//             <div
//               className="absolute overflow-hidden"
//               style={{
//                 left: "1mm",
//                 top: "1mm",
//                 width: "208mm",
//                 height: "295mm",
//               }}
//             >
//               <img
//                 src={page.images[0]}
//                 alt={alt}
//                 className="w-full h-full object-cover"
//               />
//             </div>
//           )}
//         </div>
//       );
//     });
//   });
// };

// /* -------------------------------------------------------------------------- */
// /*  MAIN COMPONENT                                                            */
// /* -------------------------------------------------------------------------- */

// const NewQuickBrochure = () => {
//   const { propertyid: routeId } = useParams();
//   const [searchParams] = useSearchParams();
//   const propertyid = routeId || searchParams.get("propertyid");

//   const [data, setData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");

//   useEffect(() => {
//     if (!propertyid) {
//       setError("No property id provided.");
//       setLoading(false);
//       return;
//     }

//     const controller = new AbortController();

//     const load = async () => {
//       setLoading(true);
//       setError("");
//       try {
//         const opts = { signal: controller.signal, headers: getAuthHeaders() };
//         const [propRes, amenRes] = await Promise.all([
//           fetch(`${API_BASE}/admin/properties/${propertyid}`, opts),
//           fetch(`${API_BASE}/getAmenities`, opts),
//         ]);

//         if (!propRes.ok)
//           throw new Error(`Property request failed (${propRes.status})`);
//         if (!amenRes.ok)
//           throw new Error(`Amenities request failed (${amenRes.status})`);

//         const propJson = await propRes.json();
//         const amenJson = await amenRes.json();

//         // Support both a bare object and a { success, data } wrapper
//         const property =
//           propJson?.data && !propJson.propertyname ? propJson.data : propJson;
//         const amenities = Array.isArray(amenJson)
//           ? amenJson
//           : amenJson?.data || [];

//         setData(buildBrochureData(property, amenities));
//       } catch (err) {
//         if (err.name !== "AbortError")
//           setError(err.message || "Something went wrong.");
//       } finally {
//         if (!controller.signal.aborted) setLoading(false);
//       }
//     };

//     load();
//     return () => controller.abort();
//   }, [propertyid]);

//   const amenityPages = useMemo(() => {
//     if (!data) return [];
//     const items = data.amenities;
//     return Array.from({ length: Math.ceil(items.length / 16) }, (_, i) =>
//       items.slice(i * 16, i * 16 + 16),
//     );
//   }, [data]);

//   const [quality, setQuality] = useState(75);
//   const [preparing, setPreparing] = useState(false);
//   const [progress, setProgress] = useState({ current: 0, total: 0 });

//   const handleDownload = async () => {
//     if (preparing) return;
//     setPreparing(true);

//     try {
//       const imgs = Array.from(
//         document.querySelectorAll(".print-page img[data-compress]"),
//       );
//       // Find all images that haven't been compressed yet
//       const validImgs = imgs.filter((img) => {
//         const src = img.getAttribute("src");
//         return src && !src.startsWith("data:");
//       });

//       setProgress({ current: 0, total: validImgs.length });

//       let count = 0;
//       for (const img of validImgs) {
//         const original = img.getAttribute("src");

//         // Compress the image
//         const newSrc = await safeCompressImage(original, quality);

//         if (newSrc) {
//           img.setAttribute("src", newSrc);
//         }

//         count++;
//         setProgress({ current: count, total: validImgs.length });
//       }

//       // Wait for all images and fonts to load
//       await Promise.race([
//         Promise.all([
//           ...Array.from(document.querySelectorAll(".print-page img")).map(
//             (img) =>
//               img.complete
//                 ? Promise.resolve()
//                 : new Promise((r) => {
//                     img.addEventListener("load", r, { once: true });
//                     img.addEventListener("error", r, { once: true });
//                   }),
//           ),
//           document.fonts?.ready ?? Promise.resolve(),
//         ]),
//         new Promise((r) => setTimeout(r, 30000)), // 30s timeout
//       ]);

//       const safeName = String(data?.name || "Property")
//         .replace(/[\\/:*?"<>|]/g, "")
//         .trim();
//       document.title = `${safeName}-QuickBrochure`;

//       window.print();

//       // NOTE: If you want to reset the images after printing so the user can pick a new quality and print again, uncomment the lines below:
//       // setTimeout(() => {
//       //   imgs.forEach(img => img.setAttribute("src", img.dataset.originalSrc || img.src));
//       // }, 1000);
//     } finally {
//       setPreparing(false);
//       setProgress({ current: 0, total: 0 });
//     }
//   };
//   if (loading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-white text-gray-600">
//         Loading brochure…
//       </div>
//     );
//   }

//   if (error || !data) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-white text-red-600">
//         {error || "Unable to load brochure."}
//       </div>
//     );
//   }

//   const {
//     name,
//     locationLine,
//     content,
//     frontImage,
//     villaImage,
//     propertytype,
//     conserlink,
//     destinationKey: propDestinationKey,
//     sections,
//   } = data;
//   const { contact } = STATIC;
//   const coverText = coverTextFor(propertytype);

//   return (
//     <div className="bg-white min-h-screen flex flex-col items-center py-10 gap-10 print:gap-0 print:py-0 print:p-0 print:bg-[#f8f7f6]">
//       {/* TOOLBAR — hidden when printing */}
//       <div className="print:hidden w-[210mm] flex flex-col gap-4 mb-4 px-2">
//         <div className="flex items-center justify-between gap-8">
//           {/* Quality Slider (0-100) */}
//           <div className="flex-1 flex items-center gap-4">
//             <span className="text-xs font-bold text-gray-500 uppercase tracking-wider w-10 text-right">
//               0%
//             </span>
//             <input
//               type="range"
//               min="0"
//               max="100"
//               step="1"
//               value={quality}
//               onChange={(e) => setQuality(Number(e.target.value))}
//               disabled={preparing}
//               className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black"
//             />
//             <span className="text-xs font-bold text-gray-500 uppercase tracking-wider w-10">
//               100%
//             </span>
//             <span className="text-sm font-medium text-gray-800 w-12 text-right">
//               {quality}%
//             </span>
//           </div>

//           <button
//             onClick={handleDownload}
//             disabled={preparing}
//             className="bg-black text-white text-sm px-8 py-2.5 rounded font-medium tracking-wide disabled:opacity-50 min-w-[160px]"
//           >
//             {preparing ? "Preparing PDF…" : "Download PDF"}
//           </button>
//         </div>

//         {/* Compression Progress Bar */}
//         {preparing && progress.total > 0 && (
//           <div className="w-full flex items-center gap-3">
//             <span className="text-xs text-gray-500 font-medium">
//               Compressing images...
//             </span>
//             <div className="flex-1 h-2 bg-gray-200 rounded overflow-hidden">
//               <div
//                 className="h-full bg-black transition-all duration-300"
//                 style={{
//                   width: `${(progress.current / progress.total) * 100}%`,
//                 }}
//               />
//             </div>
//             <span className="text-xs text-gray-500 font-medium min-w-[60px] text-right">
//               {progress.current} / {progress.total}
//             </span>
//           </div>
//         )}
//       </div>
//       {/* PRINT CSS */}
//       <style
//         dangerouslySetInnerHTML={{
//           __html: `
//             @page { size: A4 portrait; margin: 0; }
//             @media print {
//               html, body {
//                 width: 210mm;
//                 margin: 0;
//                 padding: 0;
//                 height: auto !important;
//                 -webkit-print-color-adjust: exact;
//                 print-color-adjust: exact;
//               }
//               body { zoom: 100%; }
//               .print-page {
//                 width: 210mm !important;
//                 min-height: 297mm !important;
//                 page-break-after: always;
//                 break-after: page;
//                 page-break-inside: avoid;
//                 break-inside: avoid;
//                 margin: 0 !important;
//               }
//               .print-page:last-of-type {
//                 page-break-after: auto !important;
//                 break-after: auto !important;
//               }
//               .shadow-md { box-shadow: none !important; }
//             }
//           `,
//         }}
//       />

//       {/* PAGE 1: COVER */}
//       <div className="print-page bg-[#f8f7f6] shadow-md w-[210mm] min-h-[297mm] relative overflow-hidden">
//         <div className="absolute top-0 right-[27.7mm] w-[1px] h-[36mm] bg-gray-400"></div>
//         <div className="absolute top-0 right-[21mm] w-[1px] h-[49.5mm] bg-gray-400"></div>

//         <div className="absolute top-[9mm] left-1/2 -translate-x-1/2">
//           <img
//             src={STATIC.logo}
//             alt="Zorba Stays"
//             className="h-[31mm] w-auto object-contain"
//           />
//         </div>

//         <h2 className="absolute top-[75mm] left-0 w-full text-center text-[15mm] leading-none PlayfairDisplay text-gray-800 tracking-wide">
//           {name}
//         </h2>

//         <p className="absolute top-[92.5mm] left-0 w-full text-center text-[4.2mm] leading-none PlayfairDisplay text-gray-800 tracking-[0.18em] uppercase">
//           {locationLine}
//         </p>

//         <p className="absolute top-[120mm] right-[20.7mm] montserrat text-[2.5mm] leading-none tracking-[0.28em] text-gray-600 uppercase">
//           {coverText}
//         </p>

//         <div className="absolute left-[20.7mm] top-[125.3mm] w-[168.6mm] h-[135.5mm] overflow-hidden">
//           {frontImage && (
//             <img
//               src={frontImage}
//               alt={name}
//               data-compress
//               className="w-full h-full object-cover"
//             />
//           )}
//         </div>

//         <div className="absolute bottom-[4.5mm] left-0 w-full flex justify-center">
//           <div className="montserrat flex justify-center items-center space-x-4 text-[2.7mm] text-gray-500 tracking-[0.12em] font-medium">
//             <a
//               href="https://zorbastays.com/"
//               target="_blank"
//               rel="noopener noreferrer"
//             >
//               <span>{contact.website}</span>
//             </a>
//             <span className="h-[3mm] w-[1px] bg-gray-400"></span>
//             <a href={`mailto:${contact.email}`}>
//               <span>{contact.email}</span>
//             </a>
//             <span className="h-[3mm] w-[1px] bg-gray-400"></span>
//             <span>{contact.phones}</span>
//           </div>
//         </div>
//       </div>

//       {/* PAGE 2: AMENITIES */}
//       {amenityPages.map((amenityGroup, pageIndex, allPages) => (
//         <div
//           key={pageIndex}
//           className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden"
//         >
//           <div className="absolute top-[25mm] left-[24mm]">
//             <h1
//               className="PlayfairDisplay text-gray-800 leading-none"
//               style={{
//                 fontSize: "14mm",
//                 fontWeight: 400,
//                 letterSpacing: "-0.03em",
//               }}
//             >
//               Amenities
//             </h1>
//             <p
//               className="montserrat text-gray-700 uppercase"
//               style={{
//                 marginTop: "4mm",
//                 fontSize: "2.4mm",
//                 letterSpacing: "0.18em",
//                 fontWeight: 500,
//               }}
//             >
//               {name} - {locationLine}
//             </p>
//           </div>

//           <div
//             className="absolute left-[20mm] right-[20mm] top-[92mm]"
//             style={{
//               display: "grid",
//               gridTemplateColumns: "repeat(4, 1fr)",
//               columnGap: "7mm",
//               rowGap: "15mm",
//             }}
//           >
//             {amenityGroup.map((item, index) => (
//               <div
//                 key={index}
//                 className="flex flex-col items-center justify-start text-center"
//                 style={{ height: "25mm" }}
//               >
//                 <div
//                   className="flex items-center justify-center"
//                   style={{ width: "17mm", height: "17mm" }}
//                 >
//                   <img
//                     src={item.imgSrc}
//                     alt={item.name}
//                     className="object-contain"
//                     style={{
//                       width: "100%",
//                       height: "100%",
//                       filter: "grayscale(1) brightness(0.35)",
//                     }}
//                   />
//                 </div>
//                 <div
//                   className="montserrat uppercase text-gray-800"
//                   style={{
//                     marginTop: "4mm",
//                     fontSize: "1.75mm",
//                     lineHeight: "1.45",
//                     letterSpacing: "0.16em",
//                     fontWeight: 500,
//                     maxWidth: "38mm",
//                   }}
//                 >
//                   {item.name}
//                 </div>
//               </div>
//             ))}
//           </div>

//           {pageIndex === allPages.length - 1 && (
//             <div
//               className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center"
//               style={{ bottom: "12mm", width: "42mm", gap: "3mm" }}
//             >
//               <div
//                 style={{
//                   width: "14mm",
//                   height: "0.25mm",
//                   backgroundColor: "#374151",
//                 }}
//               />
//               <div
//                 className="flex items-center justify-center"
//                 style={{
//                   width: "7mm",
//                   height: "7mm",
//                   border: "0.25mm solid #9ca3af",
//                   borderRadius: "50%",
//                 }}
//               >
//                 <img
//                   src={STATIC.amenitiesLogo}
//                   alt="logo"
//                   style={{
//                     width: "4mm",
//                     height: "4mm",
//                     objectFit: "contain",
//                     filter: "grayscale(1) brightness(0.35)",
//                   }}
//                 />
//               </div>
//               <div
//                 style={{
//                   width: "14mm",
//                   height: "0.25mm",
//                   backgroundColor: "#374151",
//                 }}
//               />
//             </div>
//           )}
//         </div>
//       ))}

//       {/* PAGE 3: THE VILLA */}
//       <div className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden">
//         <div className="absolute left-[20mm] top-[13mm]">
//           <div className="flex items-baseline leading-none">
//             <span
//               className="CormorantGaramond"
//               style={{ fontSize: "13.5mm", fontWeight: 600 }}
//             >
//               The
//             </span>
//             <span
//               className="CormorantItalic text-gray-800 ml-[2mm]"
//               style={{ fontSize: "13.5mm", fontWeight: 600 }}
//             >
//               {propertytype ? propertytype : "villa"}
//             </span>
//           </div>
//           <p
//             className="montserrat text-gray-700 uppercase"
//             style={{
//               marginTop: "4mm",
//               fontSize: "2.4mm",
//               letterSpacing: "0.18em",
//               fontWeight: 500,
//             }}
//           >
//             {locationLine}
//           </p>
//         </div>

//         <div
//           className="absolute overflow-hidden"
//           style={{
//             left: "20mm",
//             top: "52mm",
//             width: "170mm",
//             height: "127.5mm",
//           }}
//         >
//           {villaImage && (
//             <img
//               src={villaImage}
//               alt="Villa"
//               data-compress
//               className="w-full h-full object-cover"
//             />
//           )}
//         </div>

//         <div
//           className="absolute montserrat text-gray-800 brochure-description"
//           style={{
//             left: "20mm",
//             top: "185mm",
//             width: "170mm",
//             boxSizing: "border-box",
//             fontSize: "3.9mm",
//             lineHeight: "1.55",
//             wordSpacing: "normal",
//             letterSpacing: "normal",
//             textAlign: "left",
//           }}
//         >
//           <div
//             className="brochure-content"
//             dangerouslySetInnerHTML={{
//               __html: renderContentHtml(content),
//             }}
//           />
//         </div>
//       </div>

//       {/* PAGE 4: THE WAY WE CURATE (static) */}
//       <div className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden">
//         <div className="absolute left-[20mm] top-[15mm]">
//           <h1
//             className="CormorantGaramond leading-none"
//             style={{
//               fontSize: "13.5mm",
//               fontWeight: 600,
//               letterSpacing: "-0.03em",
//             }}
//           >
//             The way <span className="CormorantItalic">we curate.</span>
//           </h1>
//         </div>

//         <svg
//           className="absolute inset-0 w-full h-full pointer-events-none"
//           viewBox="0 0 210 297"
//           preserveAspectRatio="none"
//         >
//           <path
//             d="M 67 42 C 105 43, 163 48, 185 64 C 202 76, 194 91, 177 99 C 158 108, 129 109, 108 113 C 81 118, 58 128, 52 141 C 46 151, 57 158, 72 163 C 94 171, 126 176, 145 187 C 159 195, 164 204, 157 214"
//             fill="none"
//             stroke="#d1d5d6"
//             strokeWidth="0.7"
//             strokeLinecap="round"
//           />
//           <circle cx="67" cy="42" r="2.3" fill="#d1d5d6" />
//           <circle cx="189" cy="68" r="2.3" fill="#d1d5d6" />
//           <circle cx="113" cy="112" r="2.3" fill="#d1d5d6" />
//           <circle cx="71" cy="163" r="2.3" fill="#d1d5d6" />
//           <circle cx="157" cy="214" r="2.3" fill="#d1d5d6" />
//         </svg>

//         {STATIC.curate.map((c, i) => (
//           <div
//             key={i}
//             className="absolute"
//             style={{ left: c.left, top: c.top, width: c.width }}
//           >
//             <h2
//               className="PlayfairDisplay text-gray-800"
//               style={{ fontSize: "5.2mm", lineHeight: "1.05", fontWeight: 400 }}
//             >
//               {c.title.map((t, ti) => (
//                 <React.Fragment key={ti}>
//                   {ti > 0 && <br />}
//                   {t}
//                 </React.Fragment>
//               ))}
//             </h2>
//             <p
//               className="montserrat text-gray-700"
//               style={{
//                 marginTop: c.mt,
//                 fontSize: "3.25mm",
//                 lineHeight: "1.45",
//                 letterSpacing: "0.12em",
//               }}
//             >
//               {c.lines.map((l, li) => (
//                 <React.Fragment key={li}>
//                   {li > 0 && <br />}
//                   {l}
//                 </React.Fragment>
//               ))}
//             </p>
//           </div>
//         ))}
//       </div>

//       {/* DYNAMIC SECTION: one block per category + Photo Gallery */}
//       <ReusableGalleryLayout sections={sections} />

//       {/* WHY TRUST US */}
//       <div className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden flex flex-col justify-start px-[22mm] pt-[20mm]">
//         <h1 className="PlayfairDisplay leading-none text-[13mm] font-normal tracking-[-0.02em]">
//           {STATIC.whyTrustUs.title}
//         </h1>

//         <h2 className="montserrat font-extrabold mt-[8mm] text-[4.8mm] leading-[1.25] tracking-[-0.01em] max-w-[140mm]">
//           {STATIC.whyTrustUs.headingLine1}
//           <br />
//           {STATIC.whyTrustUs.headingLine2}
//         </h2>

//         <p className="montserrat mt-[6.5mm] text-[3.3mm] tracking-[0.12em] font-normal">
//           {STATIC.whyTrustUs.highlight}
//         </p>

//         <p className="montserrat mt-[4.5mm] text-[3.3mm] leading-[1.5] tracking-[0.12em] max-w-[166mm]">
//           {STATIC.whyTrustUs.paragraph1}
//         </p>

//         <p className="montserrat mt-[4.5mm] text-[3.3mm] leading-[1.5] tracking-[0.12em] max-w-[166mm]">
//           {STATIC.whyTrustUs.paragraph2Part1}
//           <strong className="font-extrabold">
//             {STATIC.whyTrustUs.propertiesCount}
//           </strong>
//           {STATIC.whyTrustUs.paragraph2Part2}
//         </p>

//         <div className="grid grid-cols-2 mt-[16mm] gap-x-[14mm] gap-y-[12mm]">
//           {STATIC.whyTrustUs.features.map((feature, idx) => {
//             const IconComponent = feature.icon;
//             return (
//               <div key={idx} className="flex items-start gap-4">
//                 <div className="flex-shrink-0 mt-0.5 text-[#c98a72] text-[35px]">
//                   <IconComponent />
//                 </div>
//                 <div>
//                   <h3 className="montserrat font-extrabold text-[4.2mm] tracking-[0.03em]">
//                     {feature.title}
//                   </h3>
//                   <p className="montserrat mt-[2.5mm] text-[3.20mm] leading-[1.5] tracking-[0.10em]">
//                     {feature.description}
//                   </p>
//                 </div>
//               </div>
//             );
//           })}
//         </div>
//       </div>

//       {/* OUR TRUSTED PARTNERS */}
//       <div className="print-page bg-[#f8f7f6] w-[210mm] min-h-[297mm] shadow-md relative overflow-hidden flex flex-col items-center pt-[22mm] px-[20mm]">
//         <h2 className="montserrat font-bold text-gray-800 text-[6.5mm] tracking-tight text-center">
//           {STATIC.trustedPartners.title}
//         </h2>

//         <div className="w-full h-[0.3mm] bg-[#dcdcdc] mt-[8mm]" />

//         <div className="w-full grid grid-cols-3 relative py-[8mm] gap-y-[8mm]">
//           <div className="absolute top-0 bottom-0 left-1/3 w-[0.3mm] bg-[#dcdcdc] -translate-x-1/2" />
//           <div className="absolute top-0 bottom-0 left-2/3 w-[0.3mm] bg-[#dcdcdc] -translate-x-1/2" />

//           {STATIC.trustedPartners.items.map((partner, index) => (
//             <div
//               key={index}
//               className="flex items-center justify-center px-[8mm] py-[3mm] h-[36mm]"
//             >
//               <img
//                 src={partner.logo}
//                 alt={partner.name}
//                 className="max-h-[33mm] max-w-[44mm] w-auto h-auto object-contain"
//               />
//             </div>
//           ))}
//         </div>
//       </div>

//       {/* SERVICES */}
//       {/* <div className="print-page w-[210mm] min-h-[297mm] px-12 pt-20 flex flex-col relative bg-[#f8f7f6] shadow-md">

//         {[STATIC.services.section1, STATIC.services.section2].map((s, i) => {
//           // Section 1 (Travel Guide) → always static link
//           // Section 2 (Concierge)   → DB conserlink, fallback to static link
//           const serviceLink =
//             i === 1
//               ? String(conserlink || "").trim() || STATIC.services.link
//               : STATIC.services.link;

//           return (
//             <div
//               key={i}
//               className={`max-w-[92%] ml-5 ${i === 0 ? "mb-12" : "mt-4"}`}
//             >
//               <h2 className="text-[44px] CormorantGaramond font-bold text-gray-900 mb-6">
//                 {s.first}{" "}
//                 <span className="CormorantItalic font-bold">{s.italic}</span>
//               </h2>

//               <p
//                 className="text-gray-700 montserrat text-[14px] tracking-wide text-justify mb-8 leading-relaxed"
//                 style={{ wordSpacing: "1px" }}
//               >
//                 {s.description}
//               </p>

//               <div
//                 className={`relative w-full h-36 flex flex-col items-center justify-center overflow-hidden rounded-sm bg-cover ${
//                   i === 0 ? "bg-center" : "bg-top"
//                 }`}
//                 style={{ backgroundImage: STATIC.services.bannerBg }}
//               >
//                 <div className="absolute inset-0 bg-[#d4dccf] opacity-85"></div>
//                 <h3 className="relative z-10 text-2xl font-black text-gray-900 mb-3 tracking-tight">
//                   {s.bannerText}
//                 </h3>
//                 <a href={serviceLink} target="_blank" rel="noopener noreferrer">
//                   <button className="relative z-10 bg-black text-white text-[9px] font-semibold tracking-widest px-7 py-2 uppercase">
//                     Click Here
//                   </button>
//                 </a>
//               </div>
//             </div>
//           );
//         })}

//       </div> */}

//       {/* SERVICES */}
//       <div className="print-page w-[210mm] min-h-[297mm] px-12 pt-20 flex flex-col relative bg-[#f8f7f6] shadow-md">
//         {[STATIC.services.section1, STATIC.services.section2].map((s, i) => {
//           // Section 1 → Travel Guide (destination-based, static)
//           // Section 2 → Concierge (DB first → destination → global fallback)
//           let serviceLink;
//           if (i === 0) {
//             serviceLink =
//               STATIC.travelGuideLinks[propDestinationKey] ||
//               STATIC.services.link;
//           } else {
//             serviceLink =
//               String(conserlink || "").trim() ||
//               STATIC.conciergeLinks[propDestinationKey] ||
//               STATIC.services.link;
//           }

//           return (
//             <div
//               key={i}
//               className={`max-w-[92%] ml-5 ${i === 0 ? "mb-12" : "mt-4"}`}
//             >
//               <h2 className="text-[44px] CormorantGaramond font-bold text-gray-900 mb-6">
//                 {s.first}{" "}
//                 <span className="CormorantItalic font-bold">{s.italic}</span>
//               </h2>

//               <p
//                 className="text-gray-700 montserrat text-[14px] tracking-wide text-justify mb-8 leading-relaxed"
//                 style={{ wordSpacing: "1px" }}
//               >
//                 {s.description}
//               </p>

//               <div
//                 className={`relative w-full h-36 flex flex-col items-center justify-center overflow-hidden rounded-sm bg-cover ${
//                   i === 0 ? "bg-center" : "bg-top"
//                 }`}
//                 style={{ backgroundImage: STATIC.services.bannerBg }}
//               >
//                 <div className="absolute inset-0 bg-[#d4dccf] opacity-85"></div>
//                 <h3 className="relative z-10 text-2xl font-black text-gray-900 mb-3 tracking-tight">
//                   {s.bannerText}
//                 </h3>
//                 <a href={serviceLink} target="_blank" rel="noopener noreferrer">
//                   <button className="relative z-10 bg-black text-white text-[9px] font-semibold tracking-widest px-7 py-2 uppercase">
//                     Click Here
//                   </button>
//                 </a>
//               </div>
//             </div>
//           );
//         })}
//       </div>
//       {/* ABOUT ZORBA */}
//       <div className="print-page w-[210mm] min-h-[297mm] relative flex flex-col justify-between px-16 pt-20 pb-12 overflow-hidden bg-[#fbfbfa] text-[#2c3333]">
//         <div className="flex flex-col">
//           <h1 className="text-[50px] font-bold leading-tight mb-80">
//             <span className="CormorantGaramond lowercase text-[#1e272e] tracking-tight">
//               {STATIC.aboutZorba.titlePrefix}{" "}
//             </span>
//             <span className="CormorantItalic text-[#1e272e]">
//               {STATIC.aboutZorba.titleSuffix}
//             </span>
//           </h1>

//           <h2 className="text-[34px] font-bold leading-snug mb-8">
//             <span className="CormorantGaramond text-[#1e272e]">
//               {STATIC.aboutZorba.headingPrefix}{" "}
//             </span>
//             <span className="CormorantItalic text-[#1e272e]">
//               {STATIC.aboutZorba.headingSuffix}
//             </span>
//           </h2>

//           <div className="montserrat flex flex-col space-y-6 text-[13.5px] leading-[1.8] tracking-[0.05em] text-[#333d42] pr-6">
//             {STATIC.aboutZorba.paragraphs.map((para, index) => (
//               <p key={index}>{para}</p>
//             ))}
//           </div>
//         </div>

//         <div className="w-full flex justify-center pt-8">
//           <a
//             href={`https://${contact.website}`}
//             target="_blank"
//             rel="noreferrer"
//             className="text-[13px] tracking-widest text-[#2c3333] border-b border-[#a86558] pb-[1px] hover:opacity-80 transition-opacity"
//           >
//             {contact.website}
//           </a>
//         </div>
//       </div>

//       {/* BACK COVER */}
//       <div className="print-page w-[210mm] min-h-[297mm] relative flex flex-col justify-between items-stretch px-16 pt-20 pb-10 bg-[#fbfbfa] text-[#1e272e]">
//         <div className="flex flex-col">
//           <h2 className="CormorantGaramond font-bold text-[44px] leading-[1.25] tracking-tight text-[#1e272e]">
//             <div>
//               <span>Indulge in </span>
//               <span className="CormorantItalic">the extraordinary;</span>
//             </div>
//             <div>
//               <span>life's too </span>
//               <span className="CormorantItalic">short for ordinary</span>
//             </div>
//           </h2>
//         </div>

//         <div className="flex justify-center items-center my-auto">
//           <img
//             src={STATIC.backCover.logo}
//             alt="Zorba Stays"
//             className="w-56 h-auto object-contain brightness-0"
//           />
//         </div>

//         <div className="w-full flex justify-center items-center space-x-2 text-[11px] tracking-[0.12em] text-[#333d42] font-sans">
//           <a
//             href={`https://${contact.website}`}
//             target="_blank"
//             rel="noopener noreferrer"
//             className="hover:opacity-75 transition-opacity"
//           >
//             {contact.website}
//           </a>
//           <span>|</span>
//           <a
//             href={`mailto:${contact.email}`}
//             className="hover:opacity-75 transition-opacity"
//           >
//             {contact.email}
//           </a>
//           <span>|</span>
//           <span>{contact.phones}</span>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default NewQuickBrochure;
